import { LIFE_MAP_ID } from './lifeMap';
import {
  NORTH_STAR_ASSESSMENT_ID, LIFE_BALANCE_ASSESSMENT_ID, ENERGETIC_ASSESSMENT_ID, MIND_ASSESSMENT_ID,
  JOB_ROLE_ASSESSMENT_ID, CAREER_ASSESSMENT_ID, PERSONALITY_ASSESSMENT_ID, ARCHETYPE_ASSESSMENT_ID,
  SHADOW_ASSESSMENT_ID, GROWTH_MATURITY_ASSESSMENT_ID,
} from './assessmentIds';
/**
 * The Growth Module — the MVP shape of the Roadmap's Growth Journey track.
 *
 * ⭐⭐ **IT ORDERS A CHECKLIST — IT NEVER WITHHOLDS A FEATURE.** Its job is to
 * answer "what do I do next?" while a new user learns the app. Since 2026-08-06
 * it is SEQUENTIAL (a step opens when the one before it is complete — see
 * `deriveModuleStates`), but that locks the ORDER OF A LIST and nothing else:
 * every surface a step points at stays reachable from the nav rail, by URL, and
 * from wherever it already was. Nothing here may refuse an action or block a
 * create path. See `docs/GROWTH_MODULE_SPEC.md` §2 for why that limit is hard:
 * on 2026-08-03 a live production lockout was removed from this app, built out
 * of exactly the "step N reveals feature N" idea. Revealing surfaces is a
 * SEPARATE feature (spec §7) with its own rules — the first of which is that a
 * missing unlock record must SHOW the feature, never hide it.
 *
 * ⭐ A STEP IS A FRAME, NOT A SCREEN. It states a developmental objective, names
 * the artifact that proves it, and points at the surface that already does the
 * work. One renderer, twenty-one rows — no step reimplements the Inbox, Foundation
 * or the Calendar. Only the Growth Cycle renders its own work inside the module,
 * because a cycle has no other home.
 *
 * ⭐ **THREE WORKING PARTS OF SEVEN, THEN A CODA.** Find Your Footing (0–6) ·
 * Live the Loop (7–13) · Explore Further (14–20) · Carry It Forward (21).
 * ⚠️ The 7/7/7 symmetry describes the three WORKING parts; step 21 is a closing
 * conclusion that sits after them, which is why it has its own part rather than
 * becoming an eighth `explore` member. `MODULE_STEPS` is 22, numbered 0–21. ⚠️ Explore COUNTS toward the module (owner,
 * 2026-08-06), reversing the earlier rule that left it uncounted. The cost was
 * weighed: of its seven steps three are acks, step 15 accepts a guild you create
 * yourself, and step 19 asks you to look rather than to buy — so finishing the
 * module never depends on another person, and never requires publishing in
 * public.
 *
 * ⚠️⚠️ **IDS ARE THE KEY — never rename one.** `GrowthCycle.chapterIds` stores
 * ids and resolves them through `stepForId`, silently dropping anything it
 * cannot find, so a rename is silent data loss. Merges keep the absorbed id
 * reachable via `absorbs`; `number` is presentation and may be changed freely.
 *
 * ⚠️ This REPLACES the Acts/Chapters arc (`docs/ACT_I_REMAP.md`). That model is
 * not wrong — it is the thing this MVP grows back into — but it presents a
 * coaching story, and what a first-week user needs is a map of the app.
 */

import type { View } from '../lib/viewRouter';
import type { CyclePhase } from './growthCycle';

/**
 * Steps are not one shape:
 *  - `session` — one sitting, produces an artifact (most steps).
 *  - `period`  — a STATE you occupy for weeks (step 13, "Live the First Cycle").
 */
export type StepKind = 'session' | 'period';

/**
 * ⭐⭐ A MODULE is a named body of steps; a PART is a named stretch inside one.
 * Orientation is the only module today, but the level exists so a later
 * "Expansion" can sit beside it without reshaping anything.
 *
 * ⚠️⚠️ Part ids are KEYS (persisted collapse state, and anything that later
 * records progress against a part). Titles are display-only and may be renamed
 * freely; ids may not.
 */
export interface ModulePart {
  id: string;
  title: string;
  blurb: string;
}

export interface GrowthModule {
  id: string;
  title: string;
  premise: string;
  parts: ModulePart[];
  /**
   * The step (in ANOTHER module) whose completion opens this module's first
   * step. Undefined = the first step is always open (Orientation).
   *
   * ⭐⭐ This is the ONLY link between two modules. Calibration opens after
   * Orientation's `know-thyself`, and nothing in Orientation waits on
   * Calibration (owner D1, `docs/CALIBRATION_MODULE_SPEC.md`).
   */
  opensAfter?: string;
}

/**
 * ⚠️⚠️ **PARTS MUST STAY CONTIGUOUS.** The rail renders each part by FILTERING
 * `MODULE_STEPS` on `part`, so a part whose steps interleave with another's
 * shows its group out of numeric order. A test pins non-interleaving.
 *
 * ⏳ **OWNER CONTENT — both blurbs redrafted 2026-08-11, expected to be edited.**
 * The 5→9 move sent Calendar and Inbox into `orient`, which left the old copy
 * describing the wrong steps: *"Put it in time"* opened LIVE THE LOOP while the
 * calendar step it named had just left that part.
 */
export const ORIENTATION_PARTS: ModulePart[] = [
  { id: 'orient',   title: 'Find Your Footing',
    blurb: 'What this is, what matters to you, what to build — and getting it into your days.' },
  { id: 'practice', title: 'Live the Loop',
    blurb: 'Work it, make it yours, and judge a whole cycle of it.' },
  { id: 'explore',  title: 'Explore Further',
    blurb: 'The parts of Metanoia the core loop never needed.' },
  /**
   * ⭐⭐ THE CODA, AND IT IS DELIBERATELY ITS OWN PART (owner, 2026-08-18).
   *
   * Step 21 is a closing conclusion, and `explore` is blurbed as "the parts of
   * Metanoia the core loop never needed" — a conclusion is not one of those. An
   * eighth `explore` member would also have ended the module's 7/7/7 symmetry
   * silently, inside a part, where nothing announces it.
   *
   * ⭐ As its own part the three working parts STAY at seven each, so
   * `REVIEW_MODAL_LEDE`'s "three parts of seven steps" remains true — the coda
   * sits after them rather than inside one.
   * ⚠️ Its title must not restate the single step beneath it (the
   * heading-identical-to-its-only-row problem `LIBRARY_GROUPS` documents).
   */
  { id: 'close',    title: 'Carry It Forward',
    blurb: 'What this was for, what it is not, and why putting it down can be the point.' },
];

/**
 * ⭐⭐ CALIBRATION — the second module (owner, 2026-10-05;
 * `docs/CALIBRATION_MODULE_SPEC.md`). Orientation step 16 opens it; each of the
 * ten remaining assessments gets its own step, film and reveal.
 *
 * ⚠️ Part ids are KEYS and must be unique across BOTH modules (`stepsInPart`
 * filters every step by part). The `cal-` prefix guarantees it.
 * ⚠️ The four middle titles match the Assessments library's own groups
 * (`LIBRARY_GROUPS`), so the user meets one structure in both places.
 * `cal-close` reuses Orientation's closing TITLE on purpose; titles are display.
 */
export const CALIBRATION_PARTS: ModulePart[] = [
  { id: 'cal-identity', title: 'Direction & Identity',
    blurb: 'What these instruments are for, and the one that names who you mean to become.' },
  { id: 'cal-energy',   title: 'Life & Energy',
    blurb: 'How your life is arranged, how your energy runs, and how your mind works today.' },
  { id: 'cal-role',     title: 'Talent & Role',
    blurb: 'Where your work belongs, and the part you play best in it.' },
  { id: 'cal-psyche',   title: 'Self & Psyche',
    blurb: 'Your traits, the archetypes you live by, and both sides of your inner forces.' },
  { id: 'cal-close',    title: 'Carry It Forward',
    blurb: 'Your growth as a whole, and when to come back.' },
];

export const GROWTH_MODULES: GrowthModule[] = [
  {
    id: 'orientation',
    title: 'Orientation',
    premise: 'Learn Metanoia by running one real loop through it, end to end.',
    parts: ORIENTATION_PARTS,
  },
  {
    id: 'calibration',
    title: 'Calibration',
    premise: 'Tune the instruments to yourself — one assessment at a time, each with the question it answers.',
    parts: CALIBRATION_PARTS,
    opensAfter: 'know-thyself',
  },
];

/**
 * ⚠️⚠️ STRICTLY SEQUENTIAL SINCE 2026-09-15 (owner, scope 003). A step opens
 * only when the one before it has been completed IN ORDER. Out-of-order
 * artifacts and acknowledgements keep their own completion rule, but cannot
 * bypass a locked predecessor — they take effect the moment the step is reached.
 * This reversed "completion beats locking" (2026-08-06), which let a guild
 * joined early show step 15 ✓ and open 16 while the user was at step 5.
 *
 * ⭐⭐ **It locks STEPS, never the APP.** Every surface a step points at stays
 * reachable by URL and from wherever it already was; nothing here may refuse an
 * action or block a create path. The thing ripped out on 2026-08-03 blocked
 * *creating projects* — if you find yourself making this refuse an action, that
 * is the bug coming back.
 *
 * ⚠️ Completion observed in order is remembered in `journeyReachedSteps`
 * (append-only), so deleting earlier work never closes a step already reached —
 * the job "completion beats locking" used to do. Nav reveal follows the same
 * order; surfaces already in `revealedSurfaces` stay visible.
 *
 * deriveStepState never returns locked: it describes only the step's artifacts.
 * sequenceStepStates applies the shared ordering rule to the whole list.
 */
export type StepState = 'locked' | 'available' | 'in-progress' | 'ready-for-review' | 'completed';

/**
 * The step's hero media — the thing that does most of the explaining.
 *
 * ⚠️ `undefined` renders the PLACEHOLDER frame, and that is the shipped state
 * for every step today: the media does not exist yet. `direction` is the art
 * brief for whoever makes it, and the placeholder shows it, so the empty state
 * is a commission rather than a blank box.
 */
export interface StepMedia {
  /**
   * ⚠️ `hls` is a STREAM, not a file — an `.m3u8` manifest (Cloudflare Stream).
   * Safari and iOS play it natively; every other browser needs `hls.js`, which
   * `StepHero` attaches only for this kind. `video` stays for a plain MP4.
   */
  kind: 'image' | 'video' | 'hls';
  /** Public path or URL. */
  src: string;
  /** Required — a hero that carries the explanation must carry it for everyone. */
  alt: string;
  /**
   * The still shown before playback. ⚠️ Effectively REQUIRED for video: without
   * it the frame is black until the user presses play, because nothing is
   * preloaded (see `StepHero`).
   *
   * ⭐ Prefer a LOCAL path. A poster fetched from the video host is a
   * third-party round-trip before the hero can paint, on a page whose whole job
   * is to look ready.
   */
  poster?: string;
}

/**
 * A multi-page flow that runs INSIDE the step rather than sending the user to a
 * surface — e.g. capturing inbox items without leaving "Clear Your Mind".
 *
 * ⚠️ Declaring this today renders a PLACEHOLDER, not a flow. It is deliberately
 * a promise the step page makes visible rather than a hidden TODO.
 */
export interface StepSequence {
  title: string;
  blurb: string;
}

export interface ModuleStep {
  id: string;
  /** Presentation only — safe to renumber. `id` is the stored key. */
  number: number;
  title: string;
  kind: StepKind;
  /** Which PART of its module this step belongs to. */
  part: string;
  /** What this step is FOR — the sentence the user reads first. */
  objective: string;
  /** Where the work actually happens. Undefined = the step renders its own. */
  surface?: View;
  surfaceLabel?: string;
  /**
   * localStorage keys written immediately before navigating to `surface`.
   *
   * ⚠️⚠️ A VIEW IS NOT ALWAYS A DESTINATION. A multi-tab page opens on whichever
   * tab its intent key names and falls back to its own default, so a step
   * labelled "Assessments" that only calls `setCurrentView(...)` lands the user
   * on that default. Four steps did exactly that, and steps 2 and 3 were worse:
   * they pointed at a page Assessments had already moved off.
   *
   * ⚠️ The KEY tracks the current IA and has changed twice. It was
   * `reflectionTab`, became `roadmapTrack` on 2026-08-03 when Assessments moved
   * to the Roadmap, and is `reflectionTab` again since 2026-08-19. A step's
   * `surface` and its intent key must be changed TOGETHER — a stale key is
   * silent, because the destination simply ignores a key it does not read and
   * opens on its default.
   *
   * ⭐ Same idiom the rest of the app already uses (`PurposeStatementCard`,
   * `DomainMissionSummaryCard`): the destination consumes and clears the key
   * itself, so nothing here needs to know how a track is selected.
   */
  surfaceIntent?: Record<string, string>;
  /**
   * ⭐ A Settings section (and sub) to land on, through `useSettingsDeepLink` — the
   * same one-shot link notifications use. Without it a step whose button says
   * "Help & Support" landed on Account → Profile (found 2026-10-15).
   */
  settingsTarget?: { section: string; sub?: string };
  /**
   * A SECOND destination, shown as another button beside the first.
   *
   * ⚠️⚠️ **FOR A STEP WHOSE WORK GENUINELY SPANS TWO PAGES — never as a shortcut
   * to somewhere handy.** Step 5 is the case it was added for (owner,
   * 2026-08-21): its instructions said "Open the Calendar. Build a day
   * template", and a day template cannot be built on the Calendar at all. The
   * Calendar is where an event goes; the Schedule page is where templates are
   * managed. One button for a two-page task sent everyone to the wrong half.
   *
   * ⚠️ ONE OBJECT, not a parallel `altSurface`/`altSurfaceLabel`/
   * `altSurfaceIntent` trio: three optional fields that must be set together are
   * three fields that can be set apart.
   *
   * ⚠️ The PRIMARY surface stays the step's `surface` — `stepActionKind`,
   * `whereLineFor` and the completion copy all read that one, and a step with a
   * secondary but no primary would render a second button and no first.
   */
  secondarySurface?: {
    view: View;
    /** Rendered as "Open {label}". */
    label: string;
    /** Same contract as `surfaceIntent` — see the warning there. */
    intent?: Record<string, string>;
  };
  /**
   * The hero. ⚠️ Undefined on every step today — see `StepMedia`.
   * `mediaDirection` is the art brief, and the placeholder renders it.
   */
  media?: StepMedia;
  mediaDirection?: string;
  /**
   * The long-form body: what this step is and why it sits here.
   *
   * ⚠️ Undefined falls back to `objective`, which is real content rather than a
   * placeholder — so an unwritten narrative reads as terse, never as broken.
   * Authored separately (`docs/GROWTH_JOURNEY_CONTENT_BRIEF.md`).
   */
  narrative?: string[];
  /**
   * How to actually do it. Imperative, one action each.
   *
   * ⚠️⚠️ These must describe the REAL completion rule in `deriveStepState` — a
   * step that reads "capture everything on your mind" but completes at three
   * items shows a tick the user does not believe they earned. A test pins every
   * step to having them.
   */
  instructions?: string[];
  /** A flow that runs inside the step. ⚠️ Renders a placeholder — see `StepSequence`. */
  sequence?: StepSequence;
  /**
   * The assessment this step is about (Calibration). Display only: it drives
   * the "Taken" mark, which NEVER feeds the step's state or the module's
   * progress — completion is the acknowledgement (owner D2). A tally of
   * assessments taken is exactly the to-do list the step-16 film forbids.
   */
  assessmentId?: string;
  /**
   * Ids this step ABSORBED.
   *
   * ⚠️⚠️ A cycle recorded before a merge holds the OLD id. A bare `.find`
   * returns undefined and the caller drops it, silently shrinking the record of
   * what that cycle moved through. Use `stepForId`.
   */
  absorbs?: string[];
}

/**
 * ⚠️ Steps 0, 8, 9, 12, 14, 17 and 19 have NO artifact — reading, looking at or
 * using something produces no record — so they complete on an explicit
 * acknowledgement stored in `userStore.moduleAcks` (spec §6.3, option A).
 *
 * ⚠️⚠️ This is the ONLY place in the module where a stored flag stands in for an
 * artifact, and it is deliberately a closed list. Anywhere else, "the output
 * exists" is the rule — a step that completes because a boolean was written is a
 * step that can be true about a user who did nothing.
 *
 * ⚠️⚠️ `marketplace` joined this list on 2026-08-06 (owner). It used to demand a
 * `user_downloads` row, which made finishing Orientation depend on ACQUIRING
 * someone else's item. Explore counts toward the module now, so that turned an
 * onboarding step into a transaction — see the step's own comment.
 *
 * ⚠️⚠️ `meet-your-talents` and `work-the-day` joined on 2026-08-12, and NEITHER
 * is an ack for want of trying:
 *  · `meet-your-talents` — introducing a thing leaves no artifact, and the app
 *    cannot see "I read about it". ⚠️ It was originally an ack for a DIFFERENT
 *    reason — `hasTalentAssessment` belonged to `know-thyself` and one signal
 *    may not answer for two steps. That reason retired on 2026-08-18 when
 *    `know-thyself` became the LIBRARY step and stopped reading the signal;
 *    this step stays an ack on its own merits. See its own comment.
 *  · `work-the-day` — `focusSessionStore` is an UNPERSISTED boolean, so opening
 *    Focus leaves nothing behind at all.
 * ⚠️⚠️ `know-thyself` joined on 2026-08-18 (owner) when the step was REFRAMED
 * from "take the Talent assessment" to "see the rest of the library". Talent is
 * introduced AND taken at `meet-your-talents`; this step's work is now looking
 * at what else is there, which produces no artifact — and must not, because the
 * step's whole argument is that taking none of them is a complete answer.
 * ⚠️⚠️ `live-cycle` joined on 2026-08-18 (owner) and it is the FIRST ack of a
 * different kind — worth stating plainly rather than filing under the rule above.
 * Its artifact is real and the app can see it: a cycle, reviewed. The problem is
 * that the artifact arrives **weeks** later. The module is SEQUENTIAL, so
 * completing on the review meant steps 14–20 stayed locked for the length of a
 * span — a minimum of one week and up to four — and the last step of LIVE THE
 * LOOP became a gate on the calendar rather than on the person.
 *  ⭐ So the bar gains a second clause: an ack is also right when the artifact
 *    exists but CANNOT ARRIVE INSIDE THE MODULE. Completing the step is now
 *    "I know where cycles live and how to run one"; the cycle itself carries on
 *    in Reflect at its own pace, and its review still writes a real record.
 *  ⚠️ This is NOT a licence to ack anything slow. The test is whether waiting
 *    would block work the module has no business blocking.
 * ⚠️ NINE of twenty-one are acks now. (This line read "Seven" while the list
 * already held eight — a count in prose drifts the moment the array changes, so
 * treat it as a warning about proportion, not an inventory.) Each one is a step
 * the app cannot verify, so the bar stays "no artifact exists" or "no artifact
 * can arrive in time" — never "no artifact was convenient".
 */
export const ACK_STEP_IDS =
  ['orientation', 'meet-your-talents', 'personalize', 'ai-assistant', 'work-the-day',
   'live-cycle', 'discover-people', 'know-thyself', 'domain-areas', 'marketplace',
   'journey-is-yours'] as const;

/**
 * ⭐ EVERY Calibration step is an acknowledgement (owner D2): watch the film,
 * confirm. Taking the assessment is never required.
 * ⚠️ A literal list, not `CALIBRATION_STEPS.map(...)`: `isAckStep` can run while
 * this module is still loading, before the steps array exists. A test pins the
 * two equal.
 */
export const CALIBRATION_ACK_IDS =
  ['cal-begin', 'cal-north-star', 'cal-life-balance', 'cal-energetic', 'cal-mind',
   'cal-job-role', 'cal-career-compass', 'cal-personality', 'cal-archetype', 'cal-shadow',
   'cal-growth-maturity', 'cal-tuned'] as const;

export const isAckStep = (id: string): boolean =>
  (ACK_STEP_IDS as readonly string[]).includes(id)
  || (CALIBRATION_ACK_IDS as readonly string[]).includes(id);

/** ≥3 captured clears the mental field. ⚠️ One value, one place — the Onboarding
 *  tab and this module must never carry two different numbers. */
export const BRAIN_DUMP_TARGET = 3;

/** `process-inbox` — how many captures must leave the funnel. */
export const INBOX_PROCESSED_TARGET = 3;

/**
 * Activity-log types that are NOT a completed action.
 *
 * ⚠️⚠️ `level_up` is an `ActivityType`, but it is a CONSEQUENCE of finishing
 * something rather than a thing anyone did. Counting it would tick step 9
 * ("complete one action") for a user who completed none — and level-ups are
 * logged by the XP engine, so it would fire on someone else's rules.
 *
 * ⭐ Lives here, not in the track, so the rule can be tested rather than sitting
 * inside a render closure where only a screenshot could catch it.
 */
export const NON_ACTION_ACTIVITY_TYPES: readonly string[] = ['level_up'];

/**
 * ⭐⭐ **STEP 5's RULE, IN ONE PLACE** — a live Quest carrying a Goal that
 * something is actually attached to. That link is what makes the cascade real; a
 * Goal nothing feeds is a sentence.
 *
 * ⚠️⚠️ Extracted (L5) because a SECOND copy was about to appear. `useReveal`
 * already had this expression inline, and Generate Actions now needs the same
 * answer for its outer gate — and `selectHasTalentAssessment` is the standing
 * warning about what happens next: that one reached THREE hand-rolled copies
 * before a guard found them, and they had already drifted.
 */
export interface CascadeQuest {
  status: string;
  goals: { linkType?: string | null; linkId?: string | null }[];
}

export const hasLinkedCascade = (quests: CascadeQuest[]): boolean =>
  quests.some(q => q.status !== 'archived'
    && q.goals.some(g => Boolean(g.linkType && g.linkId)));

export const isCompletedAction = (activityType: string): boolean =>
  !NON_ACTION_ACTIVITY_TYPES.includes(activityType);

export const MODULE_STEPS: ModuleStep[] = [
  // ══ CORE LOOP — steps 0–11 ════════════════════════════════════════════════
  {
    // ⭐ NEW ID. The proposal splits "sign up" from "here is what this is", and
    // it is right to: one is a form, the other is the first thing you read.
    // ⚠️⚠️ ABSORBS `enter`. Step 0 ("Sign Up and Get Set Up") was removed as
    // self-explanatory — you cannot be reading this without having signed up —
    // but its id may sit in an existing `GrowthCycle.chapterIds`, and a bare
    // delete would silently drop it from that cycle's record. Its Settings half
    // did not vanish: it became step 9, `personalize`, where it is a real act
    // rather than a restatement of the obvious.
    // ⭐ TITLE, 2026-08-07: "Introduction to Metanoia" restated the onboarding
    // the user has just finished. What is actually happening on this page is the
    // handover from that into the module. ⚠️ Display only — the id is the key.
    id: 'orientation', number: 0, title: 'Begin Your Growth Journey', kind: 'session', part: 'orient',
    objective: 'See what Metanoia is for, and what the next steps ask of you.',
    /**
     * ⭐ THE BUDGET EVERY NARRATIVE IS WRITTEN TO: a two-line objective plus ONE
     * paragraph that renders TWO lines. That is ~120–140 characters — measured,
     * not guessed: each rendered line is ~21px and the box lands at 214.
     *
     * ⚠️⚠️ Step 0 briefly had this REMOVED, and the reason is worth keeping: it
     * was the only step with a narrative at the time, which put its box at 214
     * against 136–160 everywhere else, so the module's first page looked like a
     * different page from the eighteen after it. The fix was never "step 0 has
     * less" — it was "every step has the same". Restored once steps 1–11 were
     * written, and it goes back the moment a step is left out.
     *
     * ⚠️ `docs/GROWTH_JOURNEY_CONTENT_BRIEF.md` still asks for 2–4 paragraphs
     * (~150 words). It predates the layout and is WRONG about length; that much
     * copy puts this box near 300px. Everything else in it still holds.
     */
    narrative: [
      'You have seen the idea behind Metanoia; the steps ahead are where you put it into practice on something real from your own life.',
    ],
    /**
     * ⭐ THE FIRST REAL HERO. Cloudflare Stream, HLS — Safari and iOS play it
     * natively, everyone else via `hls.js` (see `StepHero`).
     *
     * ⚠️⚠️ THE POSTER IS LOCAL AND MUST STAY LOCAL. `preload="none"` means the
     * frame is blank until someone presses play, so the poster is the only thing
     * that paints — fetching it from `cloudflarestream.com` would make the step
     * page wait on a third-party round-trip to look ready. It is 11KB, and the
     * bundle carries it (`public/` ships inside the iOS binary).
     *
     * ⚠️ The 84MB master is NOT in this repo — it lives in `media-src/`, which
     * is gitignored. Cloudflare holds the only copy that matters and builds the
     * 1080p→240p ladder from it.
     */
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/39c0e189f390ed2af8fc2e14e9c33c28/manifest/video.m3u8',
      poster: '/media/step-0-welcome-poster.jpg',
      alt: 'Welcome to Metanoia — an introduction to what the app is for.',
    },
    mediaDirection: 'The seven domains assembling into one character sheet — the app introducing itself as the HUD for a real life.',
    /**
     * ⚠️⚠️ NOT A PROCEDURE — the terms the whole module is taken on.
     *
     * Step 0 has no task, so imperative instructions here would be invented
     * work. What a reader actually needs at this point is permission: that the
     * order is a convenience, that what they make is real, and that nothing is
     * behind schedule. Its box used to be renamed to match, because a list of
     * terms under the heading "How to do it" is the same mismatch — there are no
     * headed boxes now, and these lines are authored data the page does not
     * render at all.
     *
     * ⚠️ NO "mark this complete when…" line, unlike every other step: the
     * footer's own box answers that question now, and repeating it here is the
     * page saying one thing twice — the exact habit the box vocabulary removes.
     */
    // ⚠️ ONE LINE EACH, measured rather than guessed: at two lines apiece this
    // box pushed the action and status cards off the bottom of the pane, so the
    // first page of the module hid the thing telling you what to do.
    instructions: [
      'Follow the path when it helps. Nothing depends on the order.',
      'Use something real. This is not a practice run.',
      'Keep it small. One real starting point is enough.',
      'Come back and change things as your life moves.',
      'Go at your own pace. There is no deadline here.',
    ],
    absorbs: ['enter'],
  },
  {
    // ⚠️⚠️ NO `surface`. This step's work happens INSIDE it (`sequence`), and
    // step 1 deliberately no longer offers to open the Inbox: Home's Inbox panel
    // is not revealed until step 7, and the funnel's later stages ask for
    // domains and action types this user has not met yet. Sending them there is
    // the thing splitting capture from triage was meant to avoid.
    id: 'clear-the-mind', number: 1, title: 'Clear Your Mind', kind: 'session', part: 'orient',
    objective: 'Get what’s on your mind out of your head.',
    // ⚠️ Says what the objective and the instructions do NOT: that capture is
    // commitment-free. That is the Inbox's governing principle — a captured
    // thought is not a task — and it is the thing that makes people willing to
    // write the awkward ones down.
    narrative: [
      'The Inbox takes anything, in whatever words you have. Nothing you write here commits you to doing it — a worry is as valid a capture as a task.',
    ],
    /**
     * ⭐ The second real hero (2026-08-10). Cloudflare Stream, HLS, 1:59 — the
     * same 1080p→240p ladder and the same rules as step 0's: the poster is
     * LOCAL because `preload="none"` means it is the only thing that paints
     * before someone presses play, and `preload` is a BILLING control (Stream
     * bills per minute delivered, and the browser default fetches part of the
     * file on every step-page render whether or not anyone watches).
     *
     * ⚠️ The poster is the film's own title card, pulled from Stream's
     * thumbnail endpoint at `?time=8s&height=540`. It carries burned-in text at
     * the top and bottom, which is exactly why real media renders uncapped 16:9
     * with `object-contain` — a capped frame would slice the wordmark off.
     */
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/1ac9cc6536f78637fe8ebe1f7d33e5ad/manifest/video.m3u8',
      poster: '/media/step-1-clear-the-mind-poster.jpg',
      alt: 'Clear Your Mind — why getting what is on your mind into the Inbox comes before any deeper reflection, and why capturing something commits you to nothing.',
    },
    mediaDirection: 'A cluttered surface — loose notes, a face-up phone, stickies on a screen edge — resolving into one still, ordered list. The feeling is relief, not productivity.',
    instructions: [
      'Press Begin — everything happens on this page.',
      // ⚠️ "One line is plenty" was here until 2026-08-23 (owner). It is not
      // lost — `ClearMindSequence` uses it as the input's PLACEHOLDER, which is
      // where that guidance is actually needed.
      'Write down the first thing on your mind.',
      'Keep going until nothing else surfaces.',
      // ⚠️ Kept to one line: the reason lives in the Progress box beside it, and
      // a second wrapped line here was measured pushing the page into a scroll.
      // ⚠️ "Where each one belongs is step 7." was dropped 2026-08-23 (owner) —
      // a forward reference, not part of this step's completion rule.
      `${BRAIN_DUMP_TARGET} captures is enough.`,
    ],
    sequence: {
      title: 'Capture without leaving this step',
      blurb: 'One sitting works best. Everything you add goes straight to your Inbox.',
    },
  },
  {
    // ⭐ Absorbs `vision` and `mission`: three statements, one sitting, one idea.
    id: 'purpose', number: 2, title: 'Set Your Direction', kind: 'session', part: 'orient',
    objective: 'Say why you’re growing, toward what, and how. It doesn’t have to be final.',
    // ⚠️ Answers "why three and not one", which nothing else on the page does.
    // ⚠️ It must NOT repeat "none of them is final" — the instructions and the
    // progress card both already say it, and a third copy on one screen is how
    // a page starts reading as filler.
    narrative: [
      'Three rather than one, because each answers a different question. A rough answer you can act on beats a blank page.',
    ],
    /**
     * ⭐ The third real hero (2026-08-11). Cloudflare Stream, HLS, 4:21 — same
     * rules as steps 0 and 1: LOCAL poster, because `preload="none"` makes it
     * the only thing that paints before play, and `preload` is a BILLING
     * control (Stream bills per minute delivered).
     *
     * ⚠️ The poster file is named for the step's **id** (`purpose`), not for
     * its screen title ("Set Your Direction") — the same split this step's
     * deep-link keys carry. A display name is not a key.
     */
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/1c72d7c5436821beda156e7f1c5767ca/manifest/video.m3u8',
      poster: '/media/step-2-purpose-poster.jpg',
      alt: 'Set Your Direction — why Purpose, Vision and Mission are three answers rather than one, and why a rough answer you can act on beats a blank page.',
    },
    surface: 'journal', surfaceLabel: 'Direction',
    /* ⚠️⚠️ THIS ROW HAS NOW POINTED AT `journal` TWICE, AND THE FIRST TIME IT WAS
       WRONG. It read `journal` until 2026-08-06 — a DEAD LINK left behind when
       Assessments moved to the Roadmap — was corrected to `roadmap`, and came
       back here on 2026-08-19 when Assessments moved to Reflect for real.
       ⭐ So the lesson is not "journal was right all along": a surface is only
       correct against the IA of its day, and the way to tell the difference is
       to CLICK IT, not to recognise the value. */
    surfaceIntent: {
      reflectionTab: 'assessments',
      // ⚠️ No `foundationScope` any more. It existed to hide Domain Missions and
      // Generate Actions from this step; those are their own assessment now, so
      // the Direction flow IS the statements this step asks for (D1).
      openFoundation: 'purpose',
    },
    mediaDirection: 'Three short handwritten lines appearing one under the other, then a faint line dropping from them toward everything below — direction reaching down into the rest of the app.',
    instructions: [
      'Open Direction.',
      'Write your Purpose — why you are growing at all.',
      'Write your Vision — what you are growing toward.',
      'Write your Mission — how you intend to move.',
      'All three need something. You will rewrite them later.',
    ],
    // ⚠️ NO `sequence`. An earlier draft promised to inline the three statements
    // here; the owner's call (2026-08-06) was to SCOPE the Foundation flow to
    // them instead. Inlining would have meant a second copy of `UserPurpose`,
    // `UserDirection` and the personal-mission step — real builders wired to AI
    // crafting — to keep in sync. The step page shows their live state instead.
    absorbs: ['vision', 'mission'],
  },
  {
    // ⭐⭐ Absorbs `domain-missions`, and since L4 the two are ONE ASSESSMENT.
    // The merged rule is the stricter of the two — mapped AND a mission written
    // — because the map alone leaves the next step nothing to build under.
    // ⚠️⚠️ It is NOT a Quest. Requiring one here demanded the artifact step 5
    // exists to teach, and for a reveal-enrolled user it was a loop: the Build
    // page where quests are made is revealed by THIS step completing.
    id: 'current-self', number: 3, title: 'Your Life Map — Activate a Domain', kind: 'session', part: 'orient',
    objective: 'See where your life happens, then pick one area to start in.',
    // ⚠️ TRUE TO THE MODEL, not a flourish: `constants/lifeMap.ts` takes four
    // readings per domain precisely because low attention + low condition
    // (neglected) and high attention + low condition (struggling) need opposite
    // responses. Do not simplify this to "rate each domain".
    narrative: [
      'Four readings per domain, not one score: neglect and effort-without-result produce the same low number.',
    ],
    /**
     * ⭐ The fourth real hero (2026-08-11). Cloudflare Stream, HLS, 5:24 — same
     * rules as steps 0–2: LOCAL poster, because `preload="none"` makes it the
     * only thing that paints before play, and `preload` is a BILLING control.
     *
     * ⚠️ This master tops out at **720p**, where steps 0–2 ship a 1080p rung.
     * Nothing to fix in the app — the ladder is built from whatever is uploaded
     * — but a full-width hero on a large display has less to work with.
     * ⚠️ The poster is named for the step's **id** (`current-self`), not its
     * screen title. On screen this film is called "Map Your Life"; the step is
     * "Your Life Map — Activate a Domain". A display name is not a key.
     */
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/88d7a8378bd2f57173dcfb2840fbf8f3/manifest/video.m3u8',
      poster: '/media/step-3-current-self-poster.jpg',
      alt: 'Map Your Life — how four readings per domain separate a part of your life you are neglecting from one you are working hard at and getting nowhere.',
    },
    surface: 'journal', surfaceLabel: 'Assessments',
    // ⚠️⚠️ Assessments moved Roadmap → REFLECT (2026-08-19), so the intent key is
    // `reflectionTab` again. Without it Reflect opens on `overview` — not the
    // page the user was on, but not the one the button names either.
    // ⭐ `openAssessment` names the Life Map itself, so the button lands on THAT
    // assessment rather than on the tab and a hunt through the library. It rides
    // along untouched: `AssessmentTab` consumes it wherever the tab is mounted.
    surfaceIntent: { reflectionTab: 'assessments', openAssessment: LIFE_MAP_ID },
    mediaDirection: 'A seven-point radar filling in unevenly — honest, not balanced — then one point brightening as it is chosen.',
    instructions: [
      'Take the Life Map in Assessments — 7 domains, 4 readings.',
      'Pick one domain to begin operating through. One is enough.',
      'Give that domain an objective: what it is for, in a line.',
      'Both halves are needed — the map alone builds nothing.',
    ],
    absorbs: ['domain-missions'],
  },
  {
    id: 'build-path', number: 4, title: 'Build Your Direction Cascade', kind: 'session', part: 'orient',
    objective: 'Turn your direction into something you can actually work on.',
    // ⚠️ Names what LINKING actually buys, which no other line on the page does:
    // `utils/questGoalLink.ts` derives a Goal's progress from the linked habit's
    // or task's real completions. That is the difference between a milestone
    // that moves on its own and one you keep updating by hand.
    // ⚠️ Two rendered lines, ~130 characters — the budget every narrative is
    // written to (see step 0). It answers "eleven types, must I learn them all?"
    // before the walkthrough can raise it, because the page lists them and the
    // honest answer is no.
    narrative: [
      'Eleven types sounds like a lot; you need three today. Linking is what makes a Goal move on work you record, not a number you update.',
    ],
    // ⚠️ The poster is named for the step's ID (`build-path`), not its screen
    // title — a display name is not a key.
    // ⚠️ Its title card is the 5s frame, NOT the 8s one the first four films
    // used: this one is already fading out by 8s, and that frame is dim enough
    // to read as a blank player. Sample and LOOK before trusting the timestamp.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/938556599309fd8aa074027e29ba5409/manifest/video.m3u8',
      poster: '/media/step-4-build-path-poster.jpg',
      alt: 'Build Your Direction Cascade — how a Quest, a Goal and a linked action turn a direction into work you can tick, and what each of the eleven action types is for.',
    },
    surface: 'action', surfaceLabel: 'Build',
    mediaDirection: 'Eleven shapes settling onto three shelves — long, medium, short — then one cascade drawing itself down from a domain to a single ticked box. The last rung is the point.',
    // ⚠️⚠️ ONE LINE EACH, measured. At two lines apiece this box pushes the
    // action and status cards off the bottom of the pane, so the page hides the
    // thing telling you what to do.
    // ⚠️ The last line exists because the step now OFFERS a walkthrough: the
    // button says Begin, and without this a reader reasonably assumes finishing
    // the tour is what finishes the step. It is not — the artifacts are.
    // ⚠️⚠️ MEASURED, NOT ESTIMATED: at 1600px the box is 343px wide and fits
    // ~48 characters on a line. A first draft at 57–69 characters wrapped every
    // item to two lines and CLIPPED items 4 and 5 — including "the link
    // completes this step", the one line the whole step turns on. Keep each of
    // these under ~48 characters, and re-look at the page after editing them.
    instructions: [
      'Press Begin — a tour of all eleven types.',
      'Create a Quest in a domain you activated.',
      'Add a Goal: a verb, thing, quantity, date.',
      'Link that Goal to an action — any of seven.',
      'The link completes this step, not the tour.',
    ],
    /**
     * ⚠️⚠️ **THE WALKTHROUGH DOES NOT COMPLETE THIS STEP.** It teaches the eleven
     * action types and rehearses the cascade on throwaway examples that are never
     * saved; completion still reads the real artifacts (`hasLinkedCascade`). That
     * keeps the module's founding rule — an artifact proves the step — and it is
     * why the flow's last button says "Open Build" rather than "Finish".
     * ⚠️ Declaring `sequence` makes `stepActionKind` return `'sequence'`, so this
     * step's button becomes **Begin** instead of **Open Build**. `surface` stays
     * set, because the flow hands off to it at the end.
     */
    sequence: {
      title: 'Learn the pieces before you build with them',
      blurb: 'Practice freely — nothing there is saved. The cascade you build after is what completes the step.',
    },
  },
  {
    // ⚠️ Renamed 'Your Calendar and Schedule' → 'Design Your Rhythm' (owner,
    // 2026-08-11). DISPLAY ONLY — the id stays `rhythm`, which the title now
    // matches rather than contradicts. `surfaceLabel` stays 'Calendar': the title
    // says what you are doing, the surface says where.
    id: 'rhythm', number: 5, title: 'Design Your Rhythm', kind: 'session', part: 'orient',
    objective: 'Decide when this happens. Leave room for what you can’t plan.',
    // ⚠️ Names what a TEMPLATE is, which nothing else on the page does — the
    // instructions say to build one, the objective says when. A day template is
    // reusable; a calendar entry is not, and that is the whole reason this step
    // asks for one rather than for a scheduled week.
    narrative: [
      'A template is a shape you can reuse, not a plan for one day — build it once and every week after starts from something.',
    ],
    // ⚠️ Poster named for the step's ID (`rhythm`), not its title — and the title
    // has already changed once, which is exactly why.
    // ⚠️ Its title card is the 6s frame. The timestamp VARIES per film (8s on
    // steps 0–3, 5s on step 4); here 5s is lit but the ring motif is only
    // half-drawn, so 6s is the better still. Sample and LOOK — do not reuse a
    // number from the last step.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/4688d9409c7316f03ee3910e9e7b2d94/manifest/video.m3u8',
      poster: '/media/step-5-rhythm-poster.jpg',
      alt: 'Design Your Rhythm — how a day template, a schedule and a loadout fit together: the day you plan, the days you put it on, and the balance you meant to strike.',
    },
    surface: 'calendar', surfaceLabel: 'Calendar',
    /**
     * ⚠️⚠️ **THE VIEW ID OF THE SCHEDULE PAGE IS `home`** — and the view id of the
     * page the rail calls "Home" is `dashboard`. The two are crossed, and this is
     * the kind of pairing that gets "corrected" by someone reading only one of
     * them: `setCurrentView('schedule')` is not a compile error waiting to
     * happen, it is not a view at all. See `viewRouter.VIEWS`.
     * ⭐ No `intent` key: the Schedule page has no tabs to land on.
     */
    secondarySurface: { view: 'home', label: 'Schedule' },
    mediaDirection: 'A day column filling with a few blocks — and staying mostly empty. The white space is the subject of the shot.',
    /**
     * ⚠️⚠️ **THESE SAID "OPEN THE CALENDAR. BUILD A DAY TEMPLATE" AND A DAY
     * TEMPLATE CANNOT BE BUILT ON THE CALENDAR** (owner, 2026-08-21). The
     * Calendar applies templates and holds events; templates are managed from
     * the Schedule page ("Manage Templates" → the `templates` view). The step
     * named the right artifact and the wrong room — a step's copy is a claim
     * about the code, and this one was false.
     */
    instructions: [
      'Open the Calendar and create an event.',
      'Navigate to the Schedule page.',
      'Build a day template — an ordinary day, not a perfect one.',
      'Leave slack. A plan with none breaks on Tuesday.',
      'Saving one template completes this step.',
    ],
  },
  {
    id: 'process-inbox', number: 6, title: 'Organize What Is Calling for Attention', kind: 'session', part: 'orient',
    objective: 'Sort what you captured, now there’s somewhere for it to go.',
    // ⚠️ Answers "why is this not step 1" — the reason lives on step 1's page
    // ("you sort it into your life at step 6") but was nowhere on THIS one, which
    // is where a user who started here is standing.
    // ⚠️ That copy named step 7 until 2026-09-18: this step was renumbered and
    // step 1's sentence was not, so it pointed at the step AFTER the one that
    // does the sorting. A step's copy is a claim about the product — if this
    // number moves again, step 1's line moves with it.
    narrative: [
      'Sorting comes after capture because every decision here needs domains and action types you had not met when you wrote these down.',
    ],
    // ⚠️ Poster named for the step's ID (`process-inbox`), not its title.
    // ⚠️ Its title card is the 6s frame — the FOURTH distinct timestamp across
    // seven films (8s on 0–3, 5s on 4, 6s on 5 and here). At 8s the same card is
    // still lit but the hairlines carry chromatic noise. Sample and LOOK.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/05fa57ecb440c47cc4e1ac1de1538e57/manifest/video.m3u8',
      poster: '/media/step-6-process-inbox-poster.jpg',
      alt: 'Organize What Is Calling for Attention — why the Inbox is unlimited while your active actions are not, and the six questions every capture answers before it becomes real work.',
    },
    surface: 'inbox', surfaceLabel: 'Inbox',
    mediaDirection: 'One undifferentiated pile sorting into labelled destinations — and a few items going to Parked and Archived, which are shown as valid endings, not failures.',
    instructions: [
      'Open the Inbox.',
      'Take each capture and decide what it actually is.',
      'Route it, park it, or archive it — all three count.',
      `${INBOX_PROCESSED_TARGET} items out of the funnel completes this step.`,
    ],
  },
  {
    // ⭐ NEW ID.
    id: 'first-progress', number: 7, title: 'Trace Your Progress', kind: 'session', part: 'practice',
    objective: 'Finish something, and see how the app records it.',
    // ⚠️ States the product's premise, which no other line on this page does:
    // XP comes from recorded work and nothing is awarded for reading a screen.
    // That is what makes the number worth looking at, and it is the claim the
    // whole character sheet rests on.
    narrative: [
      'Nothing is awarded for reading a screen. The number moves because you finished something real.',
    ],
    // ⚠️ The poster is named for the step's ID (`first-progress`), never its
    // title — the title changed on 2026-08-12 and the id is the key.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/4433a52c90b08181ea9a4ebca8f5bfbf/manifest/video.m3u8',
      poster: '/media/step-7-first-progress-poster.jpg',
      alt: 'Trace Your Progress — why nothing is awarded for reading a screen, and how one finished action becomes XP, a bar and a rank on the character sheet.',
    },
    surface: 'status', surfaceLabel: 'Status',
    mediaDirection: 'A single checkbox being ticked, and the same moment landing on the character sheet as XP, a bar, a rank. Cause and effect in one frame.',
    instructions: [
      'Finish any action you have — a task, a habit, anything.',
      'Open Status and find where it was recorded.',
      'One completed action completes this step.',
    ],
  },
  {
    // ⭐ NEW ID (owner, 2026-08-12) — Talent is INTRODUCED here.
    //
    // ⚠️⚠️ IT IS ALSO WHERE TALENT IS TAKEN, since 2026-08-18. `know-thyself`
    // used to be the step that required the assessment, which is why this one
    // had to be an ack: one signal (`hasTalentAssessment`) may not answer for
    // two steps, and state is DERIVED so nothing would have stopped it. That
    // step is now about the rest of the LIBRARY and no longer reads the signal.
    //
    // ⚠️⚠️ THIS STEP STAYS AN ACK ANYWAY, and the reason is now its own: the
    // step's job is that the assessment is not a stranger, and "I have seen what
    // it measures" leaves nothing behind for the app to check. Completing it on
    // `hasTalentAssessment` would also make the step a gate on a 13-question
    // quiz in the middle of LIVE THE LOOP, which is not what it is for.
    //
    // ⚠️⚠️ ITS WORK HAPPENS ON `roadmap:assessment-library`, WHICH MOVED FOR IT.
    // The library used to be revealed by `join-guild` — a step AFTER this one —
    // which would have sent a reveal-enrolled user to look at an assessment that
    // was not there. It is now revealed by `first-progress`, the step directly
    // before this. See `REVEALED_BY`.
    // ⚠️ Retitled "Meet Your Talents" → "Discover Your Talent Mix" (owner,
    // 2026-08-13). DISPLAY ONLY — `meet-your-talents` is the key `ACK_STEP_IDS`,
    // `REVEALED_BY` and every persisted ack are written against; renaming it
    // would un-complete this step for everyone. See `display-name-is-a-storage-key`.
    // ⭐ The new title names the ARTIFACT the step points at: the assessment
    // produces a mix (a weighted profile), not a shortlist of talents.
    id: 'meet-your-talents', number: 8, title: 'Discover Your Talent Mix', kind: 'session', part: 'practice',
    // ⚠️ "You are not taking it yet" is carried by the INSTRUCTIONS, not here —
    // the first draft said it in both places and made this the longest objective
    // in the module (129 of a 130 budget, against a median of 87). A step whose
    // objective runs three lines where every other runs two is the About box
    // changing shape, which is the thing the five-container layout exists to stop.
    objective: 'See what your talents are, and how they fit the direction you wrote.',
    // ⚠️ Says what a TALENT is, which neither the objective nor the instructions
    // do — and the objective cannot, because "what it measures" is the thing the
    // user is going to look at.
    narrative: [
      'A talent is what you find easy that others find hard, not what you got good at by practising. Nothing asks you to take it yet.',
    ],
    // ⚠️ Poster named for the step's ID (`meet-your-talents`), never its title —
    // the title changed on 2026-08-13 and the id is the key.
    // ⭐ Its title card carries the NEW title, so the film and the step agree
    // (unlike step 3, whose card says something the step does not).
    // ⚠️ Card sampled at 5s / 6s / 8s / 10s and LOOKED at: 5 and 8 are both
    // settled, 10 is already into content. 8s chosen.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/d1eb25651d8dec4943043ba4331be365/manifest/video.m3u8',
      poster: '/media/step-8-meet-your-talents-poster.jpg',
      alt: 'Discover Your Talent Mix — what the Talent assessment measures, and why a talent is what comes easily to you rather than what you built by practising.',
    },
    surface: 'journal', surfaceLabel: 'Assessments',
    // ⚠️ Without this Reflect falls back to `overview`. Same trap
    // `know-thyself` documents — a view is not always a destination.
    surfaceIntent: { reflectionTab: 'assessments' },
    mediaDirection: 'The same person doing two things — one effortful, one effortless — and only the second lighting up. What comes easily, named.',
    instructions: [
      'Open Assessments and find the Talent assessment.',
      'Read what it measures. A later step asks you to take it.',
      'Looking leaves no record — mark this done yourself.',
    ],
  },
  {
    // ⭐ NEW ID — the Settings half of the deleted step 0, moved to where it is
    // an ACT rather than a restatement. ⚠️ An ACK step: "make it yours" has no
    // single artifact (a theme, a language, a renamed domain, a notification
    // switch are all valid answers), and inventing one would pick a favourite.
    /**
     * ⚠️⚠️ **THE ID STAYS `personalize`** though the step is no longer about
     * personalisation (owner, 2026-08-13). It is the key in `ACK_STEP_IDS` and
     * in every persisted `userStore.moduleAcks`; renaming it would un-complete
     * this step for everyone who has finished it.
     *
     * ⚠️⚠️ **AND IT STAYS AN ACK.** Completing it on "you actually sent
     * feedback" is tempting — a real artifact rather than a seventh ack — but
     * state here is DERIVED, so every account that already completed step 9
     * would instantly un-complete, never having sent any. It would also make
     * feedback a toll on finishing onboarding, which is how you harvest a pile
     * of "good app" and learn nothing.
     *
     * ⭐ WHY IT WAS REFRAMED. The old step asked the user to open Settings and
     * change the theme, the language, and what each domain is called. Two
     * problems: personalising an app is self-explanatory and did not need a
     * step of its own, and Settings has now been ungated entirely (see
     * `moduleReveal`) because people want their avatar and name on day one, not
     * at step 9. The old narrative also went FALSE on 2026-08-13 — it told the
     * reader to rename their domains, and domain names stopped being editable
     * that same day.
     */
    /**
     * ⭐⭐ **THIS STEP OPENS THE CHANNEL; IT DOES NOT ASK FOR A VERDICT.** The
     * distinction is the whole design (owner, 2026-08-13). By step 9 a reader
     * has seen enough to hit a bug or a confusing screen, but twelve steps
     * remain — asking them to judge Metanoia here asks for an opinion they do
     * not yet have the evidence for.
     *
     * ⭐ The inverse matters more. If the channel is introduced only at the
     * END, every problem hit between here and there goes unreported, by a user
     * who had nowhere to put it. **Introduce support early enough to be useful;
     * ask for the review late enough to be informed.**
     *
     * ⚠️ An earlier draft was titled "Tell Us What Is Wrong" — a verdict in the
     * title while the instructions asked only that the reader learn where we
     * are. The two halves disagreed, and the title was the wrong one.
     */
    id: 'personalize', number: 9, title: 'Help Shape Metanoia', kind: 'session', part: 'practice',
    objective: 'Find the two ways to reach us. Metanoia expects to be revised too.',
    /**
     * ⭐⭐ THE SYMMETRY IS THE POINT, and it is the thing the objective and the
     * instructions cannot say: the loop this module has been teaching is the
     * loop the product claims to run on. A system built on reflection and
     * adaptation that would not submit to either is only selling the idea.
     * 📏 146 chars — inside the 103–168 spread the other twenty occupy. An
     * earlier draft ran 232 and made this About box a third taller than every
     * other, which is the page changing shape.
     * ⚠️ ONE paragraph, like all twenty others. A second would do the same.
     */
    narrative: [
      'This journey asks you to act, watch what happens, and change your approach. We are meant to work the same way — and we only see what you tell us.',
    ],
    // ⚠️ Poster named for the step's ID (`personalize`), never its title — the
    // step was reframed from personalisation to feedback on 2026-08-14 and the
    // id is the key that every persisted `moduleAcks` entry is written against.
    // ⭐ Its title card reads "STEP 9 / HELP SHAPE METANOIA", so the film was
    // produced AFTER the reframe and the card and the step agree (unlike step 3).
    // ⭐⭐ The film states this step's symmetry in its own words at 10s: "Any
    // system that asks you to reflect, learn, and adapt should be willing to do
    // the same itself" — the narrative above and the film are making one claim.
    // ⚠️ Card sampled at 4s / 5s / 6s / 8s / 10s and LOOKED at: at 4s the
    // gradient rule is still drawing, 6s and 8s are both settled, 10s is already
    // into content. 6s chosen.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/0cc79d47c95ee496799da619855ad13b/manifest/video.m3u8',
      poster: '/media/step-9-personalize-poster.jpg',
      alt: 'Help Shape Metanoia — the two ways to reach us, and why a system built on reflection and adaptation has to submit to both itself.',
    },
    // ⚠️ Points at Help & Support, which is ungated — so this step can never
    // send a reveal-enrolled user to a place that is not there.
    surface: 'settings', surfaceLabel: 'Help & Support',
    settingsTarget: { section: 'help-support', sub: 'support' },
    mediaDirection: 'Two loops turning side by side — one traced with the user\'s own cycle, one with the product\'s. The same shape, drawn twice, each feeding the other.',
    instructions: [
      /**
       * ⚠️ The two channels are genuinely different and the copy says which is
       * which: Support & Requests writes a real TICKET (status, and replies
       * from the team), while Feedback is the seven-measure review. Calling
       * both of them "feedback" would hide the one that gets an answer.
       */
      'Open Settings → Help & Support.',
      'Requests open a ticket; Feedback rates the experience.',
      'Send something if it already bothers you. If not, fine.',
      'Mark this done once you know where to reach us.',
    ],
  },
  {
    // ⭐ NEW ID. ⚠️ Scoped to SENDING A MESSAGE, not applying a proposal: 8 of
    // the 9 AI actions debit coin, and a step that charges a brand-new user real
    // currency to advance is a step that punishes following the guide.
    //
    // ⚠️⚠️ THIS STEP'S COST IS ONE BOOLEAN AWAY FROM CHANGING, AND NOT IN CODE.
    // Verified 2026-08-05 against the live DB: `chat_message` is
    // `price_cp: 30, active: FALSE`, and `spend_coin` selects
    // `where key = p_action_key AND active`, so an inactive key finds no row and
    // returns free. It is the ONLY one of the nine that is inactive.
    // → Turning `active` on in the admin console silently makes this onboarding
    //   step cost 30cp for every new user. If that is ever wanted, this step
    //   must move to an ack (`ACK_STEP_IDS`) first.
    //
    // ⚠️⚠️ MOVED 5 → 9 (owner, 2026-08-11), and it took the WALLET with it:
    // `nav:wallet` and `status:wallet` were revealed by THIS step, so a
    // reveal-enrolled user met coin here rather than four steps earlier.
    // ⚠️⚠️ **THAT PAIRING IS OVER (owner, 2026-08-21): the wallet now arrives at
    // step 1** and the sign-up Coin is announced on step 2. The old reasoning —
    // "coin arrives with the first thing that spends it" — was about SPENDING,
    // and the sign-up grant lands server-side at first load, so the balance
    // existed from step 0 with nowhere on the rail to see it. This step still
    // reveals `nav:assistant`. See `REVEALED_BY`.
    id: 'ai-assistant', number: 10, title: 'Meet the AI Assistant', kind: 'session', part: 'practice',
    objective: 'Meet the assistant — a thinking partner, not a decision-maker.',
    // ⚠️⚠️ SAYS NOTHING ABOUT PRICE, and must not start. Whether asking costs
    // anything is `ai_action_prices.chat_message` — a TABLE row the console
    // flips with no deploy, and it has flipped twice. Copy promising a free
    // conversation goes false on a toggle; copy quoting a number goes false on
    // an edit. What is safe is what the assistant can SEE and who decides, both
    // properties of the app.
    // ⭐⭐ The narrative carries the two things the objective and instructions
    // cannot: the boundary of its sight, and that a suggestion is only ever a
    // suggestion. Those are the film's spine (owner, 2026-08-14).
    narrative: [
      'It can see your direction, domains and talents — not your journal. And a suggestion is still a suggestion: you decide what fits.',
    ],
    surface: 'assistant', surfaceLabel: 'Assistant',
    mediaDirection: 'Someone thinking out loud, and the assistant turning a rough half-formed thought into something they can look at — then the person rewriting it. The last word is theirs.',
    // 🎬 Step 10's film — 3:21 (201.9s), full 1080p→240p ladder (both the 1080p
    // and 240p rungs sum to the same 201.9s).
    // ⚠️⚠️ **BY FAR THE SHORTEST FILM SINCE STEP 1** (1:59), against a script
    // targeting ~8:00 — the ten before it ran 4:20 · 1:59 · 4:21 · 5:24 · 6:30 ·
    // 7:30 · 7:48 · 9:35 · 9:52 · 4:34. `docs/STEP_10_VIDEO_SCRIPT.md` no longer
    // describes what was shot; treat the film as the source of truth.
    // ⚠️ Card sampled at 4s / 5s / 6s / 8s / 10s and LOOKED at: at 4s the
    // gradient rule is NOT yet drawn, 6s and 8s are both fully settled, 10s is
    // already into content (a sphere beside a blank input). 6s chosen — the
    // SIXTH distinct timestamp across ten films, so the number is never reusable.
    // ⚠️ Burned-in subtitles again (visible at 10s), so `object-contain` in
    // StepHero stays load-bearing — cropping this video crops its words.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/ee4109e359aa0a5431f7a9e51c8e7325/manifest/video.m3u8',
      poster: '/media/step-10-ai-assistant-poster.jpg',
      alt: 'Meet the AI Assistant — a thinking partner rather than a decision-maker: what it can see, what it cannot, and why a suggestion is still only a suggestion.',
    },
    instructions: [
      'Open the Assistant if you like. Nothing requires it.',
      'It helps you think: clarify an idea, compare two options.',
      'It proposes, you decide. Reject anything that is not you.',
      'Mark this done when you know what it is for.',
    ],
  },
  {
    // ⭐ NEW ID.
    id: 'first-reflection', number: 11, title: 'How Are You Feeling?', kind: 'session', part: 'practice',
    objective: 'Note how you’re doing today. The app works better when it knows.',
    // ⚠️ Says what the entry does NOT have to be. An entry here is a reading,
    // and the fastest way to stop someone writing one is to let them think it is
    // a piece of writing being judged.
    narrative: [
      'One honest entry is worth more than a good one. This is a reading you are taking, not a piece of writing you are grading.',
    ],
    surface: 'journal', surfaceLabel: 'Reflect',
    mediaDirection: 'A short entry being written, and a quiet reading of energy and mood settling beside it. Noticing, not diagnosing.',
    // 🎬 Step 11's film — 8:44 (524.0s), full 1080p→240p ladder (the 1080p and
    // 240p rungs both sum to 524.0s). Opens on "You have spent much of this
    // Journey looking outward", which is the turn the step is named for.
    //
    // ⚠️ THIS IS THE SECOND CUT (owner, 2026-08-16). It REPLACES uid
    // `f83318efca54f9dc3bdd6aa3e5fe06b7` (6:13), which was committed in
    // `bcfdd96` but never pushed — so no user ever saw it. The opening and the
    // title card are unchanged; the new cut is ~2.5 minutes longer.
    // ⚠️ The poster was RE-PULLED from this uid rather than kept. Two films can
    // share an opening and still differ frame by frame, and a poster from the
    // superseded upload would be a still of a video that is no longer there.
    //
    // ⚠️ Card re-sampled at 4s / 5s / 6s / 8s / 10s and LOOKED at for THIS cut,
    // not carried over: at 4s the gradient rule is still undrawn and the corner
    // motifs are half-lit, 6s and 8s are settled, 10s is into content. 6s again
    // — which is a coincidence to re-verify, never a number to reuse.
    // ⚠️ Burned-in subtitles again (visible at 10s) → `object-contain` stays
    // load-bearing.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/a086cc92001489553844fdf93ce9bf67/manifest/video.m3u8',
      poster: '/media/step-11-first-reflection-poster.jpg',
      alt: 'How Are You Feeling? — taking a reading of energy, mood and focus, and why it is a reading you take rather than a piece of writing you grade.',
    },
    instructions: [
      'Open Reflect.',
      'Write one entry about where you actually are today.',
      'One entry that you wrote completes this step.',
    ],
  },
  {
    // ⭐ NEW ID (owner, 2026-08-12) — the two ways to actually spend a day, and
    // they are ONE step because they are two answers to the same problem: Focus
    // for the day as planned, Free mode for the day as it turned out.
    //
    // ⚠️⚠️ AN ACK, BECAUSE NOTHING SURVIVES. `focusSessionStore` is a single
    // UNPERSISTED boolean (`active`) — Focus derives everything else from the
    // live schedule and the wall clock, so once the session ends or the page
    // reloads there is no trace that Focus was ever opened.
    // ⚠️⚠️ THIS COMMENT USED TO BE WRONG, AND THE STEP'S COPY WITH IT. It said
    // "Free mode's half DOES leave something — it writes a completed event".
    // It did not. `FreeTimeBlock` held its activities in component `useState`
    // with ZERO store writes, so one navigation away from Home destroyed the
    // whole log — while the narrative below promised "a day that went
    // differently still leaves a record". Fixed 2026-08-16: `freeActivityStore`
    // persists the working list and a completion writes a real `activity_logs`
    // row tagged `metadata.source = 'free-mode'`.
    //
    // ⭐ So Free mode's half IS now checkable — the tag this comment asked for
    // ("nothing marks that event as having come from Free mode") exists.
    // ⚠️ THE STEP STAYS AN ACK ANYWAY, because FOCUS's half still leaves nothing
    // and the step needs both. A rule on the free-mode tag alone would complete
    // this step for someone who never opened Focus.
    //
    // ⚠️ Placed AFTER the reflection step and immediately before the cycle: it
    // is the tool the span is actually lived with, so it wants to be the last
    // thing learned before living one. Its work happens on Home, revealed at
    // step 4 — no deadlock. See `STEP_WORKS_ON`.
    // ⚠️⚠️ RENAMED "Work the Day in Front of You" → "Navigate Your Day"
    // (owner, 2026-08-16). DISPLAY ONLY — the id stays `work-the-day`, which
    // keys ACK_STEP_IDS, moduleReveal's `nav:dashboard` entry, STEP_WORKS_ON
    // and every test that asserts this step's position. A display name is not
    // a key. [[display-name-is-a-storage-key]]
    // ✅ The rename was made BEFORE the film was shot, and it reached the card:
    // the shipped film's title card reads "STEP 12 — NAVIGATE YOUR DAY"
    // (confirmed by looking at the frame — see the media comment below). Step 3's
    // film says "Map Your Life" while its step says something else, and that
    // mismatch is visible to every user; this one was caught in time because the
    // rename landed while the film was still unshot.
    id: 'work-the-day', number: 12, title: 'Navigate Your Day', kind: 'session', part: 'practice',
    objective: 'Work the block in front of you, or log what you did instead.',
    // ⚠️ Names why the SECOND half exists. The objective describes both modes
    // and the instructions say how to reach them; neither admits that the plan
    // breaking is the normal case rather than the failure case.
    narrative: [
      'A plan that survives the day intact is rare. Free mode is there so a day that went differently still leaves a record.',
    ],
    surface: 'dashboard', surfaceLabel: 'Home',
    mediaDirection: 'One block filling the screen with everything else gone; then the same day going sideways and being written down as it actually happened. Two answers to one day.',
    // 🎬 Step 12's film — 7:17 (437.4s), full 1080p→240p ladder. Script =
    // `docs/STEP_12_VIDEO_SCRIPT.md`.
    //
    // ⭐⭐ ITS TITLE CARD READS "STEP 12 — NAVIGATE YOUR DAY" — verified by
    // LOOKING at the frame, not by trusting the filename. The 2026-08-16 rename
    // reached the card, so this film says what its step says. Step 3's does not
    // ("Map Your Life" over a step called something else), and the only fix
    // there is a re-cut — which is exactly why this was checked before wiring.
    //
    // ⚠️ Card sampled at 4s / 5s / 6s / 8s / 10s and LOOKED at, every frame:
    // at 4s the gradient rule is still undrawn and the corner block motifs are
    // half-formed; 5s, 6s and 8s are all settled and near-identical; 10s is
    // already into content (a teal sphere under the burned-in line "Earlier,
    // you gave your time some structure"). **6s** used — the middle of the
    // settled window, clear of the draw-in below and the content above.
    // ⚠️ Step 4's card was already FADING by 8s, so the top of the window is not
    // free either. The number is never reusable; sample and look, every film.
    //
    // ⚠️ Burned-in subtitles again (visible at 10s) → `object-contain` stays
    // load-bearing here. Cropping this video would crop its words.
    // ⚠️ Poster is named for the step's ID (`work-the-day`), not its screen
    // title — the title is display-only. [[display-name-is-a-storage-key]]
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/4550b1532376041dff8476740ec4b151/manifest/video.m3u8',
      poster: '/media/step-12-work-the-day-poster.jpg',
      alt: 'Navigate Your Day — working the block in front of you in Focus mode, and why a day that went another way still gets written down rather than counting as a failure.',
    },
    instructions: [
      'On Home, start Focus on the block you are in.',
      'Switch to Free mode to log what was never planned.',
      'Try both, then mark this done — Focus leaves no record.',
    ],
  },
  {
    // ⭐ Absorbs the three other cycle frames — they were always ONE record asked
    // about three ways. As one step it simply changes with the cycle's phase
    // instead of making the user pick which frame they are in.
    id: 'live-cycle', number: 13, title: 'Live the First Cycle', kind: 'period', part: 'practice',
    objective: 'Live it for a few days, then judge the cycle rather than the days.',
    // ⚠️ Names what the FLOOR is for. `constants/growthCycle.ts` treats it as the
    // minimum that survives the worst week — the field that makes a cycle
    // recoverable instead of abandoned — and the review asks about it first.
    narrative: [
      'The floor is the part that matters — a cycle you can hold through a bad week is one you finish rather than abandon.',
    ],
    // ⚠️⚠️ THE CYCLE UI USED TO RENDER INSIDE THIS STEP PAGE (`CycleChapter`,
    // full width below both columns) while `CyclesTab` rendered the same thing
    // in Reflect. Two surfaces for one record is what let their reviews drift
    // apart in the first place. The step is now a FRAME like every other step:
    // it says what a cycle is for and sends you to the one place it lives.
    // [[two-doors-one-record-disagree]]
    surface: 'journal', surfaceLabel: 'Reflect',
    // Opens Reflect on the Cycles tab rather than its overview. ⚠️ Consumed
    // one-shot by `JournalView`'s mount initializer, which validates it against
    // `REFLECTION_TABS` — so a typo here lands on the overview rather than
    // throwing. The tab is revealed at step 11, so it is always there by now.
    surfaceIntent: { reflectionTab: 'cycles' },
    mediaDirection: 'A span of days passing as one continuous shot — good days and bad ones — then the whole span judged at once at the end. Never a daily scorecard.',
    // 🎬 Step 13's film — 6:21 (381.2s), full 1080p→240p ladder. Script =
    // `docs/STEP_13_VIDEO_SCRIPT.md`.
    //
    // ⚠️ Card sampled at 4s / 5s / 6s / 8s / 10s and LOOKED at, every frame: at
    // 4s the gradient rule is absent and the corner motifs are unlit boxes; 5s
    // is settled but the left motif is still coming up; **6s** is the peak —
    // rule fully drawn, both motifs at full glow; by 8s their borders are
    // already dimming; 10s is into content (a sphere under the burned-in line
    // "Everything you have done so far in this Journey could be completed in a
    // sitting"). ⚠️ The number is never reusable — sample and look, every film.
    // ⭐ The corner motifs are DAY STRIPS, which is the one step where that
    // reads as the subject rather than decoration.
    //
    // ⚠️ Burned-in subtitles again → `object-contain` stays load-bearing here.
    // Cropping this video would crop its words.
    // ⚠️ Poster is named for the step's ID (`live-cycle`), not its screen title.
    // [[display-name-is-a-storage-key]]
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/5addcdbacb50986bf2aba34ae4b92730/manifest/video.m3u8',
      poster: '/media/step-13-live-cycle-poster.jpg',
      alt: 'Live the First Cycle — choosing a span to live deliberately, setting a floor that survives a bad week, and judging the whole span at the end rather than any single day.',
    },
    // ⚠️⚠️ AN ACK, and the instructions have to match or they promise a gate the
    // step no longer applies. They still describe the whole practice — that is
    // what the step teaches — but the LAST line is the honest one: the module
    // does not wait for the span. See `ACK_STEP_IDS` for why this ack is a
    // different kind from the others.
    instructions: [
      'Open Reflect → Cycles and plan one: intent, span, floor.',
      'Begin it and live the span. It carries on in Reflect.',
      'Answer the weekly check-in — the final review reads it.',
      'Mark this done once you know where cycles live.',
    ],
    absorbs: ['prepare-cycle', 'reflect', 'complete-cycle'],
  },

  // ══ EXPLORE — steps 14–20, introduced but never required ═══════════════════
  {
    // ⚠️⚠️ VIEWING, not posting. This is the only step whose completion would be
    // PUBLIC and irreversible; every other step writes private data. Requiring a
    // post means the module cannot be finished without publishing.
    id: 'discover-people', number: 14, title: 'Discover Other Players', kind: 'session', part: 'explore',
    objective: 'See what other people are working on. Post only if you want to.',
    // ⚠️ Says what the feed IS, which nothing else on the page does. The
    // instructions cover the reading and the not-posting.
    narrative: [
      'What reaches the feed is work people actually recorded, not what they say they are doing — which reads differently from a timeline.',
    ],
    surface: 'discover', surfaceLabel: 'Discover',
    // 🎬 Step 14's film — 5:27, full 1080p→240p ladder. Script =
    // `docs/STEP_14_VIDEO_SCRIPT.md`.
    // ⚠️ Card sampled at 4/5/6/8/10s and LOOKED at: at 4s the gradient rule is
    // undrawn, 5s is lit but the rule is still dim, 6s and 8s are both settled,
    // and 10s is already into content. 6s used.
    // ⭐ The card reads "STEP 14" and the step's own title — confirmed by opening
    // the frame, not by trusting the filename. A uid carries no hint of which film
    // it is; step 12 was nearly wired with step 11's.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/cd5872147d1e378d32a650b38cae7712/manifest/video.m3u8',
      poster: '/media/step-14-discover-people-poster.jpg',
      alt: 'Discover Other Players — reading a feed of work people actually recorded, and why posting here is genuinely optional.',
    },
    mediaDirection: 'A feed of other people’s real progress, seen from the outside. Nobody is performing; the viewer is a reader, not yet a poster.',
    instructions: [
      'Open Discover.',
      'Look at what other people are working on.',
      'Post only if you want. Nothing else here is public.',
      'Mark this done when you have seen the feed.',
    ],
  },
  {
    // ⚠️ Requires another party's space, so it stays out of the core meter.
    id: 'join-guild', number: 15, title: 'Join a Guild', kind: 'session', part: 'explore',
    objective: 'Find a group working on something you care about.',
    // ⚠️ THREE rendered lines on purpose: this objective is one line, so the
    // paragraph carries the extra one and the box matches the other steps.
    // ⚠️ Does NOT repeat "a guild you create counts" — the instruction below and
    // the progress card both already say it.
    narrative: [
      'A guild is shared work, not a chat room — what matters is what its members build. Small and quiet is a good guild.',
    ],
    surface: 'guilds', surfaceLabel: 'Guilds',
    // 🎬 Step 15's film — 6:33, full 1080p→240p ladder. Script =
    // `docs/STEP_15_VIDEO_SCRIPT.md`.
    // ⚠️ Card sampled at 4/5/6/8/10s and LOOKED at: at 4s the gradient rule is
    // undrawn, 5s is lit but the rule is still dim, 6s and 8s are both settled,
    // and 10s is already into content. 6s used.
    // ⭐ The card reads "STEP 15" and the step's own title — confirmed by opening
    // the frame, not by trusting the filename. A uid carries no hint of which film
    // it is; step 12 was nearly wired with step 11's.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/40f93e72d5ef37a994a54de28e45c0cd/manifest/video.m3u8',
      poster: '/media/step-15-join-guild-poster.jpg',
      alt: 'Join a Guild — finding a group with shared work rather than a chat room, and why a guild you create yourself counts.',
    },
    mediaDirection: 'A small group with one shared pursuit between them — a handful of people, not a crowd.',
    instructions: [
      'Open Guilds.',
      'Find a group working on something you care about.',
      'Or start your own — that counts too.',
    ],
  },
  {
    // ⚠️⚠️ REFRAMED (owner, 2026-08-18) — THE LIBRARY, NOT TALENT. This step used
    // to require the Talent assessment, which is introduced AND taken at
    // `meet-your-talents`. Its job now is to show what ELSE is in the library and
    // to say plainly that none of it is owed. It is therefore an ACK — see
    // `ACK_STEP_IDS` — because "I have seen what is there" leaves no artifact,
    // and requiring one would contradict the step's own argument.
    //
    // ⚠️ NOTHING ALREADY DONE IS ASKED FOR AGAIN. Three of the twelve active
    // assessments are behind the user by now: Direction / Purpose-Vision-Mission
    // (step 2, `AssessmentTab` → FOUNDATION_STEPS), the Life Map (step 3) and
    // Talent (step 8). The instructions name them so the screen reads as mostly
    // finished rather than mostly outstanding.
    //
    // ⭐⭐ IT OPENS CALIBRATION NOW (owner, 2026-10-05). It used to reveal the
    // whole rest of the library at once — ten unexplained instruments. It now
    // opens the second module, where each assessment has its own step and film
    // (`docs/CALIBRATION_MODULE_SPEC.md`). Still an ACK; still reveals the
    // Domain Progression track and the domain page for step 17. Orientation
    // carries on with step 17 at once — Calibration never holds it up (D1).
    // ⚠️ The film (below) predates this and still shows the full library. Kept
    // until its replacement is shot (owner D6).
    id: 'know-thyself', number: 16, title: 'Know Thyself', kind: 'session', part: 'explore',
    // ⚠️ No "each with its own film": Calibration's steps have no films yet (2026-10-16).
    objective: 'Open Calibration: the rest of the assessments, one at a time, each with the question it answers.',
    // ⚠️ Says WHEN to take one, which neither the objective nor the instructions
    // do — and it is the only guidance that makes a library of ten usable.
    narrative: [
      'The right time to take one is when you have the question it answers — not in order, and not all of them.',
    ],
    // ⚠️ A SAME-PAGE destination: this step renders on the Roadmap already, so
    // the intent is written AND announced (`goToSurface`) — `journeyModule` opens
    // the Calibration module, `roadmapTrack` makes sure the Journey track shows.
    surface: 'roadmap', surfaceLabel: 'Calibration',
    surfaceIntent: { roadmapTrack: 'journey', journeyModule: 'calibration' },
    // 🎬 Step 16's film — 6:30, full 1080p→240p ladder. Script =
    // `docs/STEP_16_VIDEO_SCRIPT.md`.
    // ⚠️ Card sampled at 4/5/6/8/10s and LOOKED at: at 4s the gradient rule is
    // undrawn, 5s is lit but the rule is still dim, 6s and 8s are both settled,
    // and 10s is already into content. 6s used.
    // ⭐ The card reads "STEP 16" and the step's own title — confirmed by opening
    // the frame, not by trusting the filename. A uid carries no hint of which film
    // it is; step 12 was nearly wired with step 11's.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/d8c086873ae741614d163273a85e2a08/manifest/video.m3u8',
      poster: '/media/step-16-know-thyself-poster.jpg',
      alt: 'Know Thyself — the rest of the assessment library, and why it is a shelf to come back to rather than a queue to work through.',
    },
    mediaDirection: 'A shelf of instruments, each for a different question — most of them left where they are, one picked up because it was needed.',
    instructions: [
      'Watch the film.',
      'Mark this done: Calibration opens beside Orientation.',
      'Orientation carries on with step 17 at once.',
    ],
  },
  {
    // ⚠️ Follows the DOMAIN work of step 4, not breathwork. Opening Domain Areas
    // with a breathwork session was the one requirement in the proposal that did
    // not teach the thing it unlocked — breathwork lives under Coherence.
    // ⭐ Points at Roadmap → Domain Progression (owner, 2026-10-03): its About tab
    // lists every domain and its areas, each described, with "Open Area" links —
    // this step's whole instruction on one screen. It pointed at the Domains page
    // (2026-10-01, now retired) and before that at `status` (the domain table).
    // ⚠️ The Growth Journey is a track on the SAME page, so the intent is written
    // AND announced (`goToSurface`) — a same-view navigation alone moves nothing.
    // `roadmap:domains` is revealed at step 16, so the track is there when this
    // step arrives.
    // ⚠️⚠️ Still deliberately NOT at `domain`. `App.renderContent` reads
    // `selectedDomainId` from localStorage and renders **null** when it is
    // absent, so sending a user who has never opened a domain straight to
    // `domain` is a blank page.
    id: 'domain-areas', number: 17, title: 'Explore Domain Areas', kind: 'session', part: 'explore',
    objective: 'Go deeper into a domain you activated. Its areas hold the detail.',
    // ⚠️ Names what an AREA is against the domain above it, and says what you do
    // not have to do — seven domains' worth of areas is where this step could
    // read as homework.
    narrative: [
      'A domain is a heading; its areas are where the ledger actually sits. You do not need to fill them all in to get the use of one.',
    ],
    surface: 'roadmap', surfaceLabel: 'Growth Foundations', surfaceIntent: { roadmapTrack: 'domains' },
    // 🎬 Step 17's film — 4:42, full 1080p→240p ladder. Script =
    // `docs/STEP_17_VIDEO_SCRIPT.md`.
    // ⚠️ Card sampled at 4/5/6/8/10s and LOOKED at: at 4s the gradient rule is
    // undrawn, 5s is lit but the rule is still dim, 6s and 8s are both settled,
    // and 10s is already into content. 6s used.
    // ⭐ The card reads "STEP 17" and the step's own title — confirmed by opening
    // the frame, not by trusting the filename. A uid carries no hint of which film
    // it is; step 12 was nearly wired with step 11's.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/e99d5460dffc2d10fb976e6ddbc2e537/manifest/video.m3u8',
      poster: '/media/step-17-domain-areas-poster.jpg',
      alt: 'Explore Domain Areas — going one level under a domain into the areas where the ledger actually sits, without needing to fill them all in.',
    },
    mediaDirection: 'One domain opening into the areas beneath it, each with its own depth — a zoom from the map into the terrain.',
    instructions: [
      'Open Growth Foundations.',
      'Open a domain you activated and go into its areas.',
      'Mark this done when you have seen what sits under one.',
    ],
  },
  {
    // ⚠️⚠️ ONE protocol in ONE quadrant. The proposal asked for one per quadrant
    // PLUS enabling suggestions for all four — but suggestions shipped default-ON
    // for every quadrant (an absent key read as ON), so that half would have
    // ticked itself for something the user never did.
    // ⚠️ Since 2026-09-29 an unset switch is OFF (`zoneTriggerEnabled`); it is
    // still a setting, not something the user built, so it stays out of the rule.
    id: 'protocols', number: 18, title: 'Protocols', kind: 'session', part: 'explore',
    objective: 'Decide in advance what you’ll do when your energy turns.',
    // ⚠️ Says WHY in advance, which is the whole idea and appears nowhere else:
    // the moment the decision is needed is the moment it cannot be made well.
    narrative: [
      'The point of deciding in advance is that the moment you need the decision is the moment you are least able to make one.',
    ],
    surface: 'journal', surfaceLabel: 'Protocol',
    /* 🐛 ADDED 2026-08-19 — this was MISSING, and the button had always landed
       on Reflect's OVERVIEW rather than Protocol. Not a regression from the IA
       move: the rule that catches this only ever ran over steps pointing at the
       ROADMAP, so a journal step with a tab-shaped label was never examined.
       ⚠️ It fails SILENTLY and looks like a design choice — the page opens, it
       is simply the wrong tab — which is why it survived a year of clicking.
       The generalised test now covers both pages. */
    surfaceIntent: { reflectionTab: 'protocol' },
    // 🎬 Step 18's film — 5:37, full 1080p→240p ladder. Script =
    // `docs/STEP_18_VIDEO_SCRIPT.md`.
    // ⚠️ Card sampled at 4/5/6/8/10s and LOOKED at: at 4s the gradient rule is
    // undrawn, 5s is lit but the rule is still dim, 6s and 8s are both settled,
    // and 10s is already into content. 6s used.
    // ⭐ The card reads "STEP 18" and the step's own title — confirmed by opening
    // the frame, not by trusting the filename. A uid carries no hint of which film
    // it is; step 12 was nearly wired with step 11's.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/57e5b8d8355be206c1d127437edb36b4/manifest/video.m3u8',
      poster: '/media/step-18-protocols-poster.jpg',
      alt: 'Protocols — deciding in advance what you will do when your energy goes somewhere you recognise, because the moment you need the decision is the moment you can least make one.',
    },
    mediaDirection: 'A decision written down while calm, then read back later by the same person in a worse state. The point is that the choice was already made.',
    instructions: [
      'Open Protocol.',
      'Pick one quadrant: Performance, Recovery, Survival, Burnout.',
      'Write what you will do there, and switch it on.',
      'Log one check-in. Both halves are needed.',
    ],
  },
  {
    // ⚠️⚠️ AN ACK, NOT AN ACQUISITION (owner, 2026-08-06). This step used to
    // complete on `libraryItems > 0` — and `marketplaceStore.fetchLibraryItems`
    // builds that list strictly from `user_downloads`, so PUBLISHING satisfied
    // nothing and only TAKING someone else's item did. Two problems once Explore
    // began counting toward the module: finishing Orientation required a
    // transaction with another user's work, and the title promised the opposite
    // of what the rule checked. The requirement is now to LOOK, which is what
    // the step was always for.
    // ⚠️ It therefore keeps its `STEP_WORKS_ON` entry even though ack steps are
    // normally exempt — this ack MEANS "I went to the tab", so the tab has to be
    // visible by step 19. `discover:marketplace` is revealed by step 14, which
    // is earlier, so there is no deadlock.
    id: 'marketplace', number: 19, title: 'Explore the Marketplace', kind: 'session', part: 'explore',
    objective: 'See what people have published, and how coin moves.',
    // ⚠️ Says what the listings ARE. ⚠️ Does NOT repeat that taking is optional
    // or that coin moves only if you do — the instructions carry both, and this
    // is the step where an extra word about money reads as a sales pitch.
    narrative: [
      'What is published here is a real routine, program or quest someone built — you are looking at their working setup, not a brochure.',
    ],
    surface: 'marketplace', surfaceLabel: 'Marketplace',
    // 🎬 Step 19's film — 6:04, full 1080p→240p ladder. Script =
    // `docs/STEP_19_VIDEO_SCRIPT.md`.
    // ⚠️ Card sampled at 4/5/6/8/10s and LOOKED at: at 4s the gradient rule is
    // undrawn, 5s is lit but the rule is still dim, 6s and 8s are both settled,
    // and 10s is already into content. 6s used.
    // ⭐ The card reads "STEP 19" and the step's own title — confirmed by opening
    // the frame, not by trusting the filename. A uid carries no hint of which film
    // it is; step 12 was nearly wired with step 11's.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/9254135e8f6726d2b79169dc769ed1ec/manifest/video.m3u8',
      poster: '/media/step-19-marketplace-poster.jpg',
      alt: 'Explore the Marketplace — looking through the routines, day templates, quests and abilities other people have published, and why taking one is optional.',
    },
    mediaDirection: 'A routine somebody built being picked up and used by somebody else, with coin moving the other way. Two people, one working structure.',
    instructions: [
      'Open the Marketplace.',
      'See what others published — routines, programs, quests.',
      'Take something only if you want it. Coin moves only then.',
      'Mark this done when you have looked.',
    ],
  },
  {
    id: 'core-values', number: 20, title: 'Explore Core Values', kind: 'session', part: 'explore',
    objective: 'Name what matters to you, one value at a time.',
    // ⚠️ THREE rendered lines: one-line objective, same as step 13.
    // ⚠️ 49 is REAL — `CORE_VALUES` in `src/data/coreValues.ts` holds exactly
    // that many. Do not round it, and do not restate it if that list changes.
    narrative: [
      'Not a quiz with a result. You write about one value at a time — 49 in the list, and nobody works through them all.',
    ],
    surface: 'journal', surfaceLabel: 'Values',
    // ⚠️ Values moved Roadmap → Reflect with Assessments (2026-08-19).
    surfaceIntent: { reflectionTab: 'values' },
    // 🎬 Step 20's film — 7:26, full 1080p→240p ladder. Script =
    // `docs/STEP_20_VIDEO_SCRIPT.md`.
    // ⚠️ Card sampled at 4/5/6/8/10s and LOOKED at: at 4s the gradient rule is
    // undrawn, 5s is lit but the rule is still dim, 6s and 8s are both settled,
    // and 10s is already into content. 6s used.
    // ⭐ The card reads "STEP 20" and the step's own title — confirmed by opening
    // the frame, not by trusting the filename. A uid carries no hint of which film
    // it is; step 12 was nearly wired with step 11's.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/a63324f244b05290bdc45f4515d6adf6/manifest/video.m3u8',
      poster: '/media/step-20-core-values-poster.jpg',
      alt: 'Explore Core Values — reflective writing one value at a time, where the name of the value is the last thing you earn rather than the prompt you start from.',
    },
    mediaDirection: 'Many candidate values on a surface, most of them being set aside, one being kept and written about. Choosing is the work.',
    instructions: [
      'Open Values.',
      'One at a time. This is reflective writing, not a quiz.',
      'One value entry completes this step.',
    ],
  },
  {
    /**
     * ⭐⭐⭐ THE CLOSING CONCLUSION (owner, 2026-08-18) — the module's last word.
     *
     * ⚠️⚠️ AN ACK, AND IT COULD NOT BE ANYTHING ELSE. Its whole argument is that
     * you may put the System down; a step that demanded an artifact to close a
     * module about not needing the module would contradict itself in the act.
     *
     * ⭐ NO `surface`. The work is reading this page, so the step renders its own
     * — the same shape as step 0, which opens the module the way this one closes
     * it. Deliberately absent from `STEP_WORKS_ON` for that reason: there is no
     * other surface it could deadlock on.
     *
     * ⭐⭐ IT LEADS INTO THE MODULE REVIEW WITHOUT RENDERING IT. Acking this step
     * completes the module, and `ModuleReviewCard` already appears beside the
     * step list at exactly that moment. The last instruction points at it.
     * ⚠️⚠️ Do NOT also mount the review card on this page — one record with two
     * doors is how the cycle review drifted, and the card's own comment says it
     * belongs beside the list, never in it.
     *
     * ⚠️ The full narration lives in `docs/STEP_21_VIDEO_SCRIPT.md`; these five
     * boxes are the page, not the film.
     */
    id: 'journey-is-yours', number: 21, title: 'The Journey Is Yours', kind: 'session', part: 'close',
    objective: 'See where this leaves you — and why the System isn’t the point.',
    // ⚠️ Carries the idea the objective cannot: that potential is not a debt.
    // The objective says what Metanoia is not; this says what growth is not.
    narrative: [
      'There is always more you could become. That does not mean you should — growth is available, not a debt.',
    ],
    // 🎬 Step 21's film — 7:36, full 1080p→240p ladder. Script =
    // `docs/STEP_21_VIDEO_SCRIPT.md`.
    // ⚠️ Card sampled at 4/5/6/8/10s and LOOKED at: at 4s the gradient rule is
    // undrawn, 5s is lit but the rule is still dim, 6s and 8s are both settled,
    // and 10s is already into content. 6s used.
    // ⭐ The card reads "STEP 21" and the step's own title — confirmed by opening
    // the frame, not by trusting the filename. A uid carries no hint of which film
    // it is; step 12 was nearly wired with step 11's.
    media: {
      kind: 'hls',
      src: 'https://customer-0rikbks3ooel6rtu.cloudflarestream.com/9c3a627a1d034f558628b002ce854bb2/manifest/video.m3u8',
      poster: '/media/step-21-journey-is-yours-poster.jpg',
      alt: 'The Journey Is Yours — why the System was never meant to become your life, and why putting it down can be the sign that it did its job.',
    },
    mediaDirection: 'Support wheels coming off a bicycle and the rider carrying on without noticing — then two paths continuing past the frame, neither with an end in sight.',
    instructions: [
      'Read this one. Nothing to open, nothing to finish.',
      'Levels carry on past 100. No number decides when you stop.',
      'Use it while it helps. Put it down when it does not.',
      'The module review sits beside the step list.',
    ],
  },
];

/**
 * ⭐⭐ CALIBRATION'S TWELVE STEPS (owner, 2026-10-05) — spec §4.
 *
 * ⚠️⚠️ IDS ARE KEYS. They live in the SAME `moduleAcks` and `journeyReachedSteps`
 * arrays as Orientation's, so the `cal-` prefix is what keeps them from ever
 * colliding — and migration 0288's admin RPC splits the two modules by it.
 * ⚠️ Numbers are per module (0–11) and display only.
 * ⚠️ Every step is an ack (`CALIBRATION_ACK_IDS`): watch, then confirm (D2).
 * ⚠️⚠️ NEVER "unlock" in any of this copy (step-16 film rule #7), and never a
 * total of assessments taken (rule #1). A test greps for both.
 * ⚠️ `instructions` must state the REAL completion rule, so all ten assessment
 * steps say the assessment is optional and marking the step is what moves on.
 */
const assessmentInstructions = (name: string): string[] => [
  'Watch the film.',
  `${name} is on your Assessments page now.`,
  'Take it when its question is live. It is never required.',
  'Mark this step done to open the next one.',
];

const assessmentSurface = (assessmentId: string, label: string) => ({
  surface: 'journal' as View,
  surfaceLabel: label,
  // ⭐ `openAssessment` lands on THAT assessment, not the library's default —
  // the pattern step 3 (Life Map) already ships.
  surfaceIntent: { reflectionTab: 'assessments', openAssessment: assessmentId },
  assessmentId,
});

export const CALIBRATION_STEPS: ModuleStep[] = [
  {
    id: 'cal-begin', number: 0, title: 'Begin Calibration', kind: 'session', part: 'cal-identity',
    objective: 'Meet the ten instruments, and learn when to reach for one.',
    narrative: [
      'Each assessment answers one question. The right time to take one is when you have that question — not in order, and not all of them.',
    ],
    mediaDirection: 'A shelf of instruments, each for a different question. Most are left where they are; one is picked up because it was needed.',
    instructions: [
      'Watch the film.',
      'Nothing to open yet. Each later step brings one.',
      'Mark this done to begin.',
    ],
  },
  {
    id: 'cal-north-star', number: 1, title: 'Name Who You Are Becoming', kind: 'session', part: 'cal-identity',
    objective: 'Write down the person you intend to become: the qualities, the standards, and how you mean to meet hard days.',
    narrative: ['Not a personality type and not a score. It is a working statement, and you can rewrite it as you change.'],
    ...assessmentSurface(NORTH_STAR_ASSESSMENT_ID, 'North-Star Identity'),
    mediaDirection: 'A compass needle settling, not on a place but on a person sketched faintly ahead on the path.',
    instructions: assessmentInstructions('North-Star Identity'),
  },
  {
    id: 'cal-life-balance', number: 2, title: 'Find the Just-Right', kind: 'session', part: 'cal-energy',
    objective: 'See where each area of your life has too little, too much, or just enough.',
    narrative: ['Balance here does not mean more of everything. The middle of the scale is the ideal, and too much is as far off as too little.'],
    ...assessmentSurface(LIFE_BALANCE_ASSESSMENT_ID, 'Life Balance'),
    mediaDirection: 'Seven dials, each with its sweet spot in the middle. Some read under, some read over, and one is turned gently back to centre.',
    instructions: assessmentInstructions('Life Balance'),
  },
  {
    id: 'cal-energetic', number: 3, title: 'Map Your Energy', kind: 'session', part: 'cal-energy',
    objective: 'See how your energy runs across seven inner domains: underactive, overactive or centred.',
    narrative: ['The inner twin of Life Balance. It applies the same just-right idea to how things feel from the inside, not to how your life is arranged.'],
    ...assessmentSurface(ENERGETIC_ASSESSMENT_ID, 'Energetic Alignment'),
    mediaDirection: 'A column of seven lights, some dim and some glaring, settling toward an even glow.',
    instructions: assessmentInstructions('Energetic Alignment'),
  },
  {
    id: 'cal-mind', number: 4, title: 'Map Your Mind', kind: 'session', part: 'cal-energy',
    objective: 'Take a snapshot of how you think, focus, notice your own patterns, steady yourself and decide.',
    narrative: ['Your mental operating system as it runs today. It is worth knowing before you try to change how you work.'],
    ...assessmentSurface(MIND_ASSESSMENT_ID, 'Mind'),
    mediaDirection: 'The same scene through a pane of glass that fogs and clears, as a mind would see it at different settings.',
    instructions: assessmentInstructions('Mind'),
  },
  {
    id: 'cal-job-role', number: 5, title: 'Find Your Team Role', kind: 'session', part: 'cal-role',
    objective: 'Discover the role you play best on a team, in strategy, execution or support.',
    narrative: ['You share a fixed number of points across the answers to each question, so the result shows what you lean toward, not everything you can do.'],
    ...assessmentSurface(JOB_ROLE_ASSESSMENT_ID, 'Job Role'),
    mediaDirection: 'One team at three tables (planning, building, holding it together) and one person drifting to the table where they come alive.',
    instructions: assessmentInstructions('Job Role'),
  },
  {
    id: 'cal-career-compass', number: 6, title: 'Point Your Career Compass', kind: 'session', part: 'cal-role',
    objective: 'Understand the kind of work, values and environments you thrive in.',
    narrative: ['Read it beside your Talent mix and your team role. They are three angles on the same question: where your work belongs.'],
    ...assessmentSurface(CAREER_ASSESSMENT_ID, 'Career Compass'),
    mediaDirection: 'A compass on a desk, its needle swinging between office, workshop, field and studio before it settles.',
    instructions: assessmentInstructions('Career Compass'),
  },
  {
    // ⚠️ IP: the app's own five-trait model and owned archetypes only — never
    // MBTI letters, four-letter codes or 16Personalities names (film rule #4).
    id: 'cal-personality', number: 7, title: 'Your Personality Spectrum', kind: 'session', part: 'cal-psyche',
    objective: 'See your blend of five core traits, and the Metanoia archetype that captures how you show up.',
    narrative: ['No result here is good or bad. It is your own blend.'],
    ...assessmentSurface(PERSONALITY_ASSESSMENT_ID, 'Personality'),
    mediaDirection: 'Five sliders forming a chord. The same chord, played by different people, sounds different and right each time.',
    instructions: assessmentInstructions('Personality'),
  },
  {
    // ⭐ ARCHETYPE BEFORE SHADOW (owner D9). The narrative is true in this order
    // because the Matrix result is recomputed AT VIEW TIME from the latest
    // Shadow Work (`AssessmentResults` → `fetchLatestShadowMap`), so taking
    // Shadow later deepens a result already taken. ⚠️ If results are ever frozen
    // at submit time, this copy becomes false.
    id: 'cal-archetype', number: 8, title: 'Map Your Archetypes', kind: 'session', part: 'cal-psyche',
    objective: 'Find the archetypes you live by: their light, their shadow, and a path toward wholeness.',
    narrative: ['Your result grows with you. Once you take Shadow Work, the next step, this same result places you on each archetype’s matrix instead of only describing it.'],
    ...assessmentSurface(ARCHETYPE_ASSESSMENT_ID, 'Archetype Matrix'),
    mediaDirection: 'Twelve masks on a wall. One is lit from the front; its darker twin is lit from behind.',
    instructions: assessmentInstructions('Archetype Matrix'),
  },
  {
    // ⚠️ NEVER CLINICAL (film rule #5): ten inner forces, both sides, in healthy
    // form — never "find what is wrong with you". The longest instrument.
    id: 'cal-shadow', number: 9, title: 'Meet Both Sides', kind: 'session', part: 'cal-psyche',
    objective: 'See whether ten pairs of inner forces show up in their healthy form or their over-extended one.',
    narrative: ['Gentle and non-diagnostic. A strong lean is not a problem in itself; what matters is whether it shows up healthy. You can take one spectrum at a time, and your Archetype result reads deeper afterwards.'],
    ...assessmentSurface(SHADOW_ASSESSMENT_ID, 'Shadow Work'),
    mediaDirection: 'A figure and its shadow on a wall. The shadow briefly moves on its own, then the two move together.',
    instructions: assessmentInstructions('Shadow Work'),
  },
  {
    id: 'cal-growth-maturity', number: 10, title: 'Map Your Growth Stage', kind: 'session', part: 'cal-close',
    objective: 'See where you stand in your own development, and what the next stage looks like.',
    narrative: ['Last on purpose. It asks about your growth as a whole, which is easier to answer once you have looked at the parts.'],
    ...assessmentSurface(GROWTH_MATURITY_ASSESSMENT_ID, 'Growth Maturity Mapping'),
    mediaDirection: 'A spiral staircase seen from above: a figure on one turn, the next turn visible just above.',
    instructions: assessmentInstructions('Growth Maturity Mapping'),
  },
  {
    id: 'cal-tuned', number: 11, title: 'Tuned, Not Finished', kind: 'session', part: 'cal-close',
    objective: 'See what calibration was for, and when to come back to it.',
    narrative: ['Instruments drift. Retake an assessment when your life has changed enough that the old reading no longer fits.'],
    mediaDirection: 'A musician tuning between pieces: not once before the concert, but again as the room warms.',
    instructions: [
      'Watch this one. There is nothing to open.',
      'Every assessment stays on your Assessments page.',
      'Retake any of them whenever you like.',
      'Mark this done to finish Calibration.',
    ],
  },
];

/** Every step of every module. ⚠️ Banking, lookup and reveal read THIS. */
export const ALL_JOURNEY_STEPS: ModuleStep[] = [...MODULE_STEPS, ...CALIBRATION_STEPS];

/**
 * A module's own steps, in order.
 * ⚠️ `MODULE_STEPS` keeps meaning ORIENTATION's 22: the console's exporter
 * (`scripts/export-journey-steps.mjs`) scrapes it by name, and a dozen tests pin
 * it. Do not rename it to mean "all".
 */
export const STEPS_BY_MODULE: Record<string, ModuleStep[]> = {
  orientation: MODULE_STEPS,
  calibration: CALIBRATION_STEPS,
};

export const moduleSteps = (moduleId: string): ModuleStep[] => STEPS_BY_MODULE[moduleId] ?? [];

/** The module a step belongs to. */
export const moduleOfStep = (stepId: string): GrowthModule | undefined =>
  GROWTH_MODULES.find(m => moduleSteps(m.id).some(s => s.id === stepId || s.absorbs?.includes(stepId)));

/** The steps of one part, in order. */
export const stepsInPart = (partId: string): ModuleStep[] =>
  ALL_JOURNEY_STEPS.filter(s => s.part === partId);

export interface PartProgress {
  steps: ModuleStep[];
  done: number;
  total: number;
  complete: boolean;
}

export interface ModuleProgress {
  partsDone: number;
  partsTotal: number;
  stepsDone: number;
  stepsTotal: number;
  /** Every step in every part is complete. */
  complete: boolean;
}

/** How far through one part, against a derived state map. */
export function partProgress(
  partId: string,
  states: Record<string, StepState>,
): PartProgress {
  const steps = stepsInPart(partId);
  const done = steps.filter(s => states[s.id] === 'completed').length;
  // ⚠️ `steps.length > 0`: an empty part would otherwise be VACUOUSLY complete
  // (`0 === 0`), which is how a part id that no step claims — a typo, or a part
  // whose steps were moved out — reads as finished rather than as broken.
  return { steps, done, total: steps.length, complete: done === steps.length && steps.length > 0 };
}

/**
 * How far through a whole module.
 *
 * ⭐ Extracted out of `GrowthJourneyTrack` so `complete` is a RULE that can be
 * tested rather than a closure inside a component that needs a dozen stores to
 * mount. It is what the completion review reads — see `constants/moduleReview.ts`.
 *
 * ⚠️⚠️ **THIS MEASURES; IT NEVER GATES.** Nothing downstream of `complete` may
 * withhold a step, a surface or a completion. The module is already finished at
 * the moment it turns true — everything it unlocks is an OFFER.
 */
export function moduleProgress(
  m: GrowthModule,
  states: Record<string, StepState>,
): ModuleProgress {
  const steps = m.parts.flatMap(p => stepsInPart(p.id));
  const stepsDone = steps.filter(s => states[s.id] === 'completed').length;
  return {
    partsDone: m.parts.filter(p => partProgress(p.id, states).complete).length,
    partsTotal: m.parts.length,
    stepsDone,
    stepsTotal: steps.length,
    // ⚠️ Same vacuous-truth guard as above: a module with no steps is not done.
    complete: stepsDone === steps.length && steps.length > 0,
  };
}

/**
 * Resolve ANY step id — current or absorbed — to the step that represents it.
 *
 * ⚠️⚠️ Use this instead of `MODULE_STEPS.find`. A `GrowthCycle` recorded before a
 * merge holds ids like `vision` or `domain-missions` that are no longer rows.
 *
 * ⚠️ The nine unwired Act-II chapters of the old arc (`best-self`, `gap`,
 * `bottleneck`, …) are NOT carried forward and resolve to `undefined`. That is
 * safe rather than lossy: every one of them was `planned`, and the track
 * excluded planned chapters from `noteChapterCompleted`, so no cycle can ever
 * hold one. Carrying them as rows nobody derives would be the phantom-field
 * trap. Their design survives in `docs/ACT_I_REMAP.md`.
 */
export function stepForId(id: string): ModuleStep | undefined {
  return ALL_JOURNEY_STEPS.find(s => s.id === id)
    ?? ALL_JOURNEY_STEPS.find(s => s.absorbs?.includes(id));
}

// ⚠️ `chapterForId` (a deprecated alias of `stepForId`) was REMOVED here. Its
// stated reason was "kept as the name `CycleChapter` imports", and CycleChapter
// was deleted when step 13 stopped hosting the cycle — so the alias had no
// caller left and nothing to be compatible with. A deprecated export kept alive
// by its own test is a phantom. [[metanoia-os-phantom-field-trap]]

/**
 * The signals a step's completion is derived from.
 *
 * ⭐⭐ Completion is DERIVED, never stored — a step is complete because its
 * artifact EXISTS. That is what makes renumbering or re-ordering the module cost
 * a RULE rather than a migration, and it is why every account can be reset
 * without losing anything the module claims.
 */
export interface JourneySignals {
  /** Steps 1 · 12 · 15 — the acknowledged ids (see `ACK_STEP_IDS`). */
  acks: readonly string[];
  /** `clear-the-mind` — how many items have ever been captured into the Inbox. */
  inboxCaptured: number;
  /** `process-inbox` — how many have been moved past Capture, i.e. actually looked at. */
  inboxTriaged: number;
  /**
   * `process-inbox` — how many have LEFT the funnel (`status !== 'open'`).
   * ⚠️ Parked and archived count. Non-action captures never reach `routed` by
   * design, so requiring it would make the step uncompletable for anyone whose
   * brain dump was mostly thoughts rather than tasks.
   */
  inboxProcessed: number;
  /** `purpose` — Purpose, Vision and YOUR Mission (the single personal one). */
  purposeStatement: string;
  visionStatement: string;
  /** ⚠️ `userStore.missionStatement`, NOT `missions[domainId]` — the seven domain
   *  missions ship as defaults, so they prove nothing. */
  missionStatement: string;
  /** `current-self` — whether a Life Map sitting has been completed. */
  hasLifeMap: boolean;
  /**
   * `current-self` — domains that are MAPPED and carry an AUTHORED mission
   * (`isMappedAndMissioned`).
   *
   * ⚠️⚠️ **WAS `activatedDomains`, WHICH COUNTED QUESTS** (changed L4,
   * 2026-08-09). Quests are what step 5 teaches, so requiring one here made step
   * 4 impossible to finish without the artifact the NEXT step introduces — and
   * for a reveal-enrolled user that was a loop, because the Build page where
   * quests are made is itself revealed by step 4 completing.
   */
  missionedDomains: number;
  /** `build-path` — the cascade, one rung at a time: Quest → Goal → a linked action. */
  hasQuest: boolean;
  hasGoal: boolean;
  hasLinkedGoal: boolean;
  /*
   * ⚠️⚠️ `assistantMessagesSent` WAS HERE AND IS DELETED (2026-08-14, owner).
   * Step 10 completed on sending one message until `ai_action_prices.chat_message`
   * went `active` — at which point advancing through the guide charged a new user
   * real coin, which the step's own comment had forbidden. It is now an ACK, so
   * nothing derives from a message count and a signal nobody reads is the
   * phantom-field trap. Deleted rather than deprecated. Do not reintroduce it as
   * a completion rule: the film's whole argument is that the assistant is
   * optional, and a rule that requires a paid action contradicts it on screen.
   */
  /** `rhythm` — saved day templates. */
  dayTemplates: number;
  /**
   * `first-progress` — actions genuinely completed.
   * ⚠️⚠️ `ActivityType` includes `level_up`, which is a CONSEQUENCE of finishing
   * something, not a thing anyone did. Counting it would tick this step for a
   * user who completed nothing.
   */
  actionsCompleted: number;
  /** `first-reflection` — journal entries written. */
  journalEntries: number;
  /**
   * `live-cycle` — the current Growth Cycle's phase, or null when none exists yet.
   * ⚠️ `ready-for-review` is derived from the CLOCK, not a flag — that is what
   * lets the review open when the span ends rather than when someone remembers.
   */
  cyclePhase?: CyclePhase | null;
  /** `join-guild` — guilds joined or owned. */
  guildsJoined: number;
  /**
   * ⚠️⚠️ There is NO Talent signal here any more. `hasTalentAssessment` was
   * `know-thyself`'s, and it was DELETED (2026-08-18) rather than left unused
   * when that step became an ack — an unread field on this interface is a rule
   * nobody can see, and every consumer had to be re-pointed anyway.
   * ⭐ The selector itself (`selectHasTalentAssessment`) is very much alive —
   * PurposeWizard reads it to show the talent panel. Only the SIGNAL went.
   */
  /** `protocols` — protocols currently switched on, in ANY quadrant. */
  activeProtocols: number;
  /**
   * ⚠️ There is no `marketplace` signal. It reads `acks` like every other ack step —
   * `libraryItems` was DELETED rather than left unused, along with the
   * marketplace fetch the module did on mount solely to feed it.
   */
  /** `core-values` — Core Values entries written. */
  valueEntries: number;
}

/**
 * A step's state, derived from artifacts the app already produces.
 *
 * ⚠️ Returns `available` — never `locked` — for anything not yet started. The
 * module is non-blocking: a user who captured nothing has nothing to organize
 * rather than a prerequisite they failed.
 */
export function deriveStepState(stepId: string, s: JourneySignals): StepState {
  // The acknowledgement steps share one rule, stated once. ⚠️ Deliberately not
  // "the three" — that count has changed twice.
  if (isAckStep(stepId)) {
    return s.acks.includes(stepId) ? 'completed' : 'available';
  }

  switch (stepId) {
    case 'clear-the-mind': {
      if (s.inboxCaptured === 0) return 'available';
      return s.inboxCaptured >= BRAIN_DUMP_TARGET ? 'completed' : 'in-progress';
    }

    case 'purpose': {
      const statements = [s.purposeStatement, s.visionStatement, s.missionStatement];
      const has = statements.filter(t => t.trim().length > 0).length;
      if (has === 0) return 'available';
      return has === statements.length ? 'completed' : 'in-progress';
    }

    // ⭐⭐ The merged step. BOTH halves are required — see the row's comment.
    // ⚠️⚠️ The second half is a MISSION, not a Quest (L4). See `missionedDomains`.
    case 'current-self': {
      if (!s.hasLifeMap && s.missionedDomains === 0) return 'available';
      return s.hasLifeMap && s.missionedDomains > 0 ? 'completed' : 'in-progress';
    }

    // The cascade is only taught once it reaches an action. A Goal that nothing
    // is attached to is exactly the gap this step exists to close.
    case 'build-path': {
      if (!s.hasQuest) return 'available';
      if (!s.hasGoal) return 'in-progress';
      return s.hasLinkedGoal ? 'completed' : 'ready-for-review';
    }

    case 'rhythm':
      return s.dayTemplates > 0 ? 'completed' : 'available';

    case 'process-inbox': {
      if (s.inboxProcessed >= INBOX_PROCESSED_TARGET) return 'completed';
      if (s.inboxProcessed > 0 || s.inboxTriaged > 0) return 'in-progress';
      return 'available';
    }

    case 'first-progress':
      return s.actionsCompleted > 0 ? 'completed' : 'available';

    case 'first-reflection':
      return s.journalEntries > 0 ? 'completed' : 'available';

    // ⚠️ `live-cycle` USED TO DERIVE FROM `cyclePhase` here — planning/active →
    // in-progress, span over → ready-for-review, reviewed → completed. It is an
    // ACK now (see `ACK_STEP_IDS`), so `isAckStep` catches it above and this
    // case would be unreachable. `signals.cyclePhase` is still read — the step
    // page shows the phase as context — it just no longer decides the step.

    case 'join-guild':
      return s.guildsJoined > 0 ? 'completed' : 'available';

    // ⚠️⚠️ `know-thyself` HAD A CASE HERE (`hasTalentAssessment ? completed :
    // available`) and it was REMOVED, not moved: the step became an ack on
    // 2026-08-18, so `isAckStep` returns above before this switch is reached and
    // the case would be a branch that compiles, reads sensibly and never runs.

    // ⚠️ Both halves: a protocol you never test against a real reading is a
    // setting, not a practice.
    case 'protocols': {
      if (s.activeProtocols === 0 && s.journalEntries === 0) return 'available';
      return s.activeProtocols > 0 && s.journalEntries > 0 ? 'completed' : 'in-progress';
    }

    case 'core-values':
      return s.valueEntries > 0 ? 'completed' : 'available';

    default:
      // ⚠️ An unknown id (including an absorbed one, which must NOT answer for
      // itself — the step that absorbed it does) reports the neutral state.
      return 'available';
  }
}

export const STEP_STATE_META: Record<StepState, { label: string; hint: string }> = {
  locked:             { label: 'Locked',           hint: 'The step before this one is not finished' },
  /* ⚠️⚠️ **`available` AND `in-progress` MUST NOT SHARE A LABEL.** They did for
     one day (owner asked for "Ready to begin" → "In progress", 2026-09-10) and
     the chip then claimed work had been started on a step nobody had touched.
     ⭐ It is NOT the "wall of 22" the first note here feared — sequencing means
     exactly ONE step is `available` at a time and the other 21 read `Locked`
     (measured on a zeroed account: available 1, locked 21). The cost is
     narrower and steadier than that: the SEVEN acknowledgement steps have no
     in-between state at all — you have acked or you have not — so each one read
     "In progress" for its entire life until the button was pressed.
     ⭐ "Not started" rather than a return to "Ready to begin": the owner
     rejected that wording, and this keeps the three the reader already knows
     from any task list — Not started → In progress → Complete — with
     "In progress" left exactly where it is true. */
  available:          { label: 'Not started',      hint: 'Nothing is blocking this' },
  'in-progress':      { label: 'In progress',      hint: 'Started, not finished' },
  'ready-for-review': { label: 'Ready for review', hint: 'The output exists — confirm it' },
  completed:          { label: 'Complete',         hint: 'The output exists' },
};

/**
 * The shared sequence rule for module rows and navigation reveal.
 * A step opens only after its predecessor completes in order, now or in the
 * persisted history. Its own artifact never bypasses that requirement. History
 * stores completed step IDs, not positions; a regressed row keeps its own state.
 * Step 0 always opens. Callers bank only completed states after hydration.
 */
export function sequenceStepStates(
  ownState: (id: string) => StepState,
  reached: readonly string[] = [],
): Record<string, StepState> {
  const history = new Set(reached);
  const out: Record<string, StepState> = {};
  /* ⭐⭐ ONE CHAIN PER MODULE (Calibration, 2026-10-05). A single chain over every
     step would make Calibration wait for Orientation step 21 (appended) or make
     Orientation step 17 wait for all of Calibration (inserted) — both break D1.
     A module with `opensAfter` starts open only when THAT step is complete or in
     history. ⚠️ GROWTH_MODULES order matters: a module's `opensAfter` must be
     in a module processed BEFORE it (a test pins this). */
  for (const m of GROWTH_MODULES) {
    let open = m.opensAfter
      ? out[m.opensAfter] === 'completed' || history.has(m.opensAfter)
      : true;
    for (const step of moduleSteps(m.id)) {
      const own = ownState(step.id);
      const state: StepState = open ? own : 'locked';
      out[step.id] = state;
      open = state === 'completed' || history.has(step.id);
    }
  }
  return out;
}

export function deriveModuleStates(
  s: JourneySignals,
  reached: readonly string[] = [],
): Record<string, StepState> {
  return sequenceStepStates(id => deriveStepState(id, s), reached);
}

/**
 * Can the user open this step's page?
 *
 * ⭐⭐ **SEQUENTIAL SINCE 2026-08-21 (owner): a step you have not reached cannot
 * be opened.** The module has always DERIVED `locked` (`deriveModuleStates`),
 * but the rail let you click a locked row and read its page anyway — dimmed,
 * with a lock icon, deliberately: *"knowing what is coming is the whole point of
 * a map"*. The owner's call reverses that. What is lost is the preview of what
 * lies ahead; what is gained is a module that reads as a sequence rather than a
 * menu, so nobody starts at step 12.
 *
 * Completed-in-order history keeps earlier steps open even if work is deleted.
 * An artifact completed ahead of the frontier still waits its turn.
 * ⚠️ The step the user is UP TO is never locked either (its predecessor is
 * complete by definition), so there is always at least one row to click.
 *
 * ⭐ A predicate rather than an inline `=== 'locked'` at the call site: this is
 * the rule, it is worth naming, and a test can bite on it.
 */
export const isStepSelectable = (state: StepState): boolean => state !== 'locked';

/**
 * The step the user is up to: the FIRST that is not complete.
 *
 * ⭐⭐ Extracted on 2026-08-19 because Home's Growth Journey panel and the
 * Roadmap's own rail both have to answer "what next?", and they must never give
 * two answers. It was a one-line `.find` inside `GrowthJourneyTrack`; a second
 * copy on Home is exactly the drift this module keeps being bitten by.
 *
 * ⚠️ Falls back to the FIRST step, not to `undefined`, for the finished module —
 * a panel with nothing to point at would otherwise render a hole. Callers that
 * need to distinguish "done" from "at step 0" must read `moduleProgress`
 * (`complete`), which is the question they are actually asking.
 *
 * ⚠️⚠️ ORDER COMES FROM THE ARRAY, never from `number`. `number` is presentation
 * and the module has been reordered twice.
 */
export function nextOpenStep(
  states: Record<string, StepState>,
  moduleId: string = GROWTH_MODULES[0].id,
): ModuleStep {
  const steps = moduleSteps(moduleId);
  return steps.find(s => states[s.id] !== 'completed') ?? steps[0];
}

/**
 * The step that must finish for `stepId` to open, or `undefined` when it is not
 * locked. ⭐ Names the IMMEDIATE predecessor, which is literally the rule —
 * pointing further ahead would describe a different promise than the one the
 * lock makes.
 */
export function blockingStepFor(
  stepId: string,
  states: Record<string, StepState>,
): ModuleStep | undefined {
  if (states[stepId] !== 'locked') return undefined;
  const m = moduleOfStep(stepId);
  if (!m) return undefined;
  const steps = moduleSteps(m.id);
  const i = steps.findIndex(s => s.id === stepId);
  if (i > 0) return steps[i - 1];
  // ⭐ A module's FIRST step waits on another module's step (Calibration C0 on
  // Orientation's `know-thyself`) — callers name the module when it differs.
  return i === 0 && m.opensAfter ? stepForId(m.opensAfter) : undefined;
}

/**
 * Why a locked step is locked, in one line — the rail row's tooltip and the
 * step page's locked action.
 *
 * ⭐ Names the OTHER module when the wait crosses modules: Calibration's first
 * step waits on Orientation step 16, and "Opens when step 16 is complete" would
 * read as Calibration's own (nonexistent) step 16.
 */
export function lockedLabel(stepId: string, states: Record<string, StepState>): string {
  const blocker = blockingStepFor(stepId, states);
  if (!blocker) return 'Opens once the step before this one is complete';
  const own = moduleOfStep(stepId);
  const theirs = moduleOfStep(blocker.id);
  return theirs && own && theirs.id !== own.id
    ? `Opens when ${theirs.title} step ${blocker.number} is complete`
    : `Opens when step ${blocker.number} is complete`;
}

/**
 * The teaser's line for a module that has not opened: "Opens when you finish
 * Orientation step 16 — Know Thyself". ⚠️ Read from `opensAfter`, so it follows
 * the data rather than restating it.
 */
export function moduleOpensLine(m: GrowthModule): string {
  const after = m.opensAfter ? stepForId(m.opensAfter) : undefined;
  const theirs = after ? moduleOfStep(after.id) : undefined;
  return after && theirs
    ? `Opens when you finish ${theirs.title} step ${after.number} — ${after.title}`
    : 'Opens once the module before it is complete';
}
